// src/pages/RaceLive.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import './ObsTools.css';
import { Icon } from '../components/icons';
import { colorFromName, chromaSafeColor } from '../utils/colors';
import VerticalStage from '../components/obs/VerticalStage';
import { V_W, V_H, isVerticalFormat, stageBox, verticalPos, withFormat, loadObsVertical, saveObsVertical } from '../utils/obsFormat';
import raceSenseLogo from '../img/IOS1024_BG.png';
import { ThemeToggle } from '../components/ThemeContext';
import RaceSettingsPanel from '../components/RaceSettingsPanel';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:'
  ? `wss://${window.location.hostname}/ws`
  : `ws://${window.location.hostname}:5001`);

// Immagini di default (coerenti col setup gara) per il pannello DISPOSITIVI.
// Ruolo speciale nelle assegnazioni: vettura "bandiera gialla" (vedi server.js).
const FLAG_ROLE = 'FLAG_DEVICE';
const PILOT_DEFAULT = `${API_BASE}/uploads/pilotdefault.png`;
const TEAM_DEFAULT = `${API_BASE}/uploads/teamdefault.png`;

// Etichetta mostrata nelle classifiche compatte/veloci. Alcuni piloti hanno il
// cognome impostato a un placeholder (es. "/") con il nome vero nel campo nome:
// qui ripuliamo gli slash e mostriamo il nome/team. Fallback: team → tag → mac.
function displayLabel(d) {
  const cleaned = String(d?.fullName || '').replace(/[/\\]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || d?.team || d?.tag || d?.mac || '';
}

// Sigla breve (max 3 caratteri, maiuscolo) ricavata dal nome pulito: identifica
// il pilota/team nelle classifiche compatte senza mostrare il nome per intero.
function shortLabel(d) {
  const s = displayLabel(d).replace(/\s+/g, '').slice(0, 3).toUpperCase();
  return s || '—';
}

// === Canvas colors (theme-aware) ===
// Legge i valori correnti delle CSS variables del tema dal documento. Va
// richiamata all'inizio di ogni render() del canvas: i valori vengono risolti
// al volo così il cambio di tema si propaga senza bisogno di useEffect extra.
const getCanvasColors = () => {
  if (typeof document === 'undefined') {
    return {
      bg: '#0a0a0e', grid: 'rgba(255,255,255,0.035)',
      track: 'rgba(80,84,90,0.95)', outline: 'rgba(255,255,255,0.10)',
      text: '#ffffff', textShadow: 'rgba(0,0,0,0.8)',
    };
  }
  const cs = getComputedStyle(document.documentElement);
  const read = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bg:         read('--rs-canvas-bg',          '#0a0a0e'),
    grid:       read('--rs-canvas-grid',        'rgba(255,255,255,0.035)'),
    track:      read('--rs-canvas-track',       'rgba(80,84,90,0.95)'),
    outline:    read('--rs-canvas-outline',     'rgba(255,255,255,0.10)'),
    text:       read('--rs-canvas-text',        '#ffffff'),
    textShadow: read('--rs-canvas-text-shadow', 'rgba(0,0,0,0.8)'),
  };
};

// === FLAGS (aggiungi qui, sotto gli import)
const RaceFlag = ({ status, size = 18 }) => {
  const s = (status || 'IN CORSO').toUpperCase();
  const color =
    s === 'RED FLAG' ? '#ff4d4f' :
      s === 'YELLOW FLAG' ? '#f1c40f' :
        s === 'FINITA' ? null : // checkered
          s === 'FORMATION LAP' ? '#ff9800' : // arancione per formation lap
            '#21c130'; // IN CORSO / default

  if (s === 'FINITA') {
    // bandiera a scacchi
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Bandiera a scacchi" role="img">
        <defs>
          <pattern id="chk" width="4" height="4" patternUnits="userSpaceOnUse">
            <rect width="2" height="2" fill="#fff" />
            <rect x="2" y="2" width="2" height="2" fill="#fff" />
          </pattern>
        </defs>
        <path d="M4 3v18" stroke="#ddd" strokeWidth="2" />
        <path d="M6 4c4 0 6 2 10 2V4c0 0 0 0 0 0 0 2 0 4 0 6-4 0-6-2-10-2v6" fill="url(#chk)" stroke="#bbb" strokeWidth="0.5" />
      </svg>
    );
  }

  // bandiera piena colorata
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-label={`Bandiera ${s}`} role="img">
      <path d="M4 3v18" stroke="#ddd" strokeWidth="2" />
      <path d="M6 4c4 0 6 2 10 2V4c0 0 0 0 0 0 0 2 0 4 0 6-4 0-6-2-10-2v6" fill={color} />
    </svg>
  );
};

function toRad(d) { return d * Math.PI / 180; }
const formatLap = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(3);
  return `${m}:${sec.padStart(6, '0')}`;
};
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const xmlEscape = (value) => {
  if (value === undefined || value === null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const formatSeconds = (value) => (Number.isFinite(value) ? value.toFixed(3) : '');

// Formatta il countdown timer in HH:MM:SS
const formatCountdown = (seconds) => {
  if (!seconds && seconds !== 0) return '--:--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// =============================================================================
// DirectionAlertsOverlay — pannello fisso in alto a destra che mostra i
// veicoli con problemi di segnale (Dead Reckoning). Visibile SOLO in modalità
// direzione gara (!publicMode). I livelli sono:
//   - warning : dati assenti da più di 4s (marker ancora visibile sulla mappa)
//   - lost    : veicolo in fase FADEOUT (>10s), marker in dissolvenza
//   - gone    : veicolo scomparso (>12s), marker rimosso dalla mappa
// =============================================================================
// =============================================================================
// DirectionAlertsOverlay — pannello fisso in alto a destra con i veicoli che
// hanno problemi di segnale (Dead Reckoning). Visibile SOLO in direzione gara.
//   warning : dati assenti da più di 4s (marker ancora visibile sulla mappa)
//   lost    : veicolo in fase FADEOUT (>10s), marker in dissolvenza
//   gone    : veicolo scomparso (>12s), marker rimosso dalla mappa
// =============================================================================
const ALERT_LABEL = { gone: 'Segnale perso', lost: 'Segnale in dissolvenza', warning: 'Dati in ritardo' };

function DirectionAlertsOverlay({ alerts, publicMode }) {
  if (publicMode) return null;
  if (!Array.isArray(alerts) || alerts.length === 0) return null;

  return (
    <div className="lv-alerts" role="status" aria-live="polite">
      <div className="lv-alerts__head">
        <Icon.Alert size={14} />
        <span>Direzione gara · Segnale</span>
        <span className="lv-alerts__count">{alerts.length}</span>
      </div>
      {alerts.slice(0, 6).map((a) => {
        const seconds = ((a.lastSeenMs || 0) / 1000).toFixed(1);
        return (
          <div key={a.mac + '_' + a.level} className={`lv-alert lv-alert--${a.level}`}>
            <div className="lv-alert__top">
              <span className="lv-alert__dot" />
              <span className="lv-alert__label">{ALERT_LABEL[a.level] || a.level}</span>
              <span className="lv-alert__age">{seconds}s</span>
            </div>
            <div className="lv-alert__name">{a.fullName || a.tag || a.mac}</div>
            {a.team && a.team !== a.fullName && <div className="lv-alert__team">{a.team}</div>}
            <div className="lv-alert__meta">
              <span>Giro <strong>{a.lapCount ?? 0}</strong></span>
              <span>Km/h <strong>{a.lastKnownSpeedKmh ?? 0}</strong></span>
              <span>Sat <strong>{a.sats ?? 0}</strong></span>
              <span>4G <strong className={a.signal4g != null && a.signal4g < 30 ? 'is-low' : ''}>{a.signal4g != null ? `${a.signal4g}%` : '—'}</strong></span>
              {a.battery != null && <span>Bat <strong className={a.battery < 20 ? 'is-low' : ''}>{a.battery}%</strong></span>}
              {Number.isFinite(a.lastKnownLat) && Number.isFinite(a.lastKnownLon) && (
                <span className="lv-alert__coords">{a.lastKnownLat.toFixed(5)}, {a.lastKnownLon.toFixed(5)}</span>
              )}
            </div>
          </div>
        );
      })}
      {alerts.length > 6 && <div className="lv-alerts__more">+{alerts.length - 6} altri veicoli</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BANNER COLLEGAMENTO RACESENSE PULSE (vista pubblica, FORMATION LAP)
   Ogni dispositivo in gara ha un mini-form: il pilota lo riconosce dall'ID,
   inserisce il codice del suo account Pulse e lo collega. A fine gara il report
   completo (tempi, settori, curve, traiettorie, sanzioni) finisce su Firebase
   e diventa leggibile dall'app per quell'utente.
   ═══════════════════════════════════════════════════════════════════════════ */
function PulseLinkBanner({ drivers = [] }) {
  const [closed, setClosed] = useState(false);
  const [codeByMac, setCodeByMac] = useState({});
  const [statusByMac, setStatusByMac] = useState({}); // mac -> { state, msg }

  if (closed) return null;

  const setCode = (mac, v) => setCodeByMac(s => ({ ...s, [mac]: v }));

  const submit = async (mac) => {
    const code = (codeByMac[mac] || '').trim();
    if (!code) { setStatusByMac(s => ({ ...s, [mac]: { state: 'err', msg: 'Inserisci il tuo codice' } })); return; }
    setStatusByMac(s => ({ ...s, [mac]: { state: 'busy' } }));
    try {
      const r = await fetch(`${API_BASE}/api/race/pulse-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac, pulseId: code }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setStatusByMac(s => ({ ...s, [mac]: { state: 'err', msg: j.error || 'Errore' } })); return; }
      setStatusByMac(s => ({ ...s, [mac]: { state: 'ok', msg: 'Collegato!' } }));
    } catch (e) {
      setStatusByMac(s => ({ ...s, [mac]: { state: 'err', msg: e.message || 'Errore di rete' } }));
    }
  };

  const linkedCount = drivers.filter(d => d.pulseLinked || statusByMac[d.mac]?.state === 'ok').length;

  return (
    <section className="rs-card rs-card--accent lv-pulse">
      <button className="lv-pulse__close" onClick={() => setClosed(true)} aria-label="Chiudi">
        <Icon.X size={16} />
      </button>
      <div className="lv-pulse__head">
        <div className="lv-pulse__badge"><Icon.Activity size={20} /></div>
        <div className="lv-pulse__text">
          <span className="rs-kicker rs-kicker--red">RaceSense Pulse</span>
          <div className="lv-pulse__title">Sei uno dei piloti in gara?</div>
          <div className="lv-pulse__sub">
            Collega il tuo account <strong>RaceSense Pulse</strong> al tuo dispositivo: a fine gara
            ritroverai tempi, settori, tempi per curva e traiettorie nell'app.
          </div>
        </div>
        {drivers.length > 0 && (
          <span className="rs-badge rs-badge--lime">{linkedCount}/{drivers.length} collegati</span>
        )}
      </div>

      <div className="lv-pulse__list">
        {drivers.length === 0 ? (
          <div className="lv-pulse__empty">In attesa dei dispositivi in pista…</div>
        ) : drivers.map(d => {
          const st = statusByMac[d.mac] || {};
          const linked = d.pulseLinked || st.state === 'ok';
          return (
            <div className={`lv-pulse__card ${linked ? 'is-linked' : ''}`} key={d.mac}>
              <div className="lv-pulse__card-top">
                <Icon.Device size={14} />
                <span className="lv-pulse__name">{d.fullName || d.tag || '—'}</span>
                <span className="lv-pulse__mac" title={d.mac}>{d.mac}</span>
                {linked && <span className="lv-pulse__check"><Icon.Check size={12} /></span>}
              </div>
              {linked ? (
                <div className="lv-pulse__linked">Account collegato</div>
              ) : (
                <div className="rs-input-group">
                  <input
                    type="text"
                    className="rs-input rs-input--mono"
                    placeholder="Il tuo codice Pulse"
                    value={codeByMac[d.mac] || ''}
                    onChange={(e) => setCode(d.mac, e.target.value)}
                    disabled={st.state === 'busy'}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(d.mac); }}
                  />
                  <button className="rs-btn rs-btn--primary" onClick={() => submit(d.mac)} disabled={st.state === 'busy'}>
                    {st.state === 'busy' ? '…' : 'Collega'}
                  </button>
                </div>
              )}
              {st.state === 'err' && <span className="rs-error">{st.msg}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DeviceManagerPanel({ drivers = [], flagDevices = [], expanded, onToggle }) {
  const [devices, setDevices] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [teams, setTeams] = useState([]);
  const [aliases, setAliases] = useState({});
  const [pending, setPending] = useState(null); // { mac, pilotId, message }
  const [busyMac, setBusyMac] = useState(null);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState({}); // mac -> testo di ricerca pilota

  // Liste master (una tantum): piloti, team, alias MAC→nome.
  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`${API_BASE}/api/pilots`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/teams`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/device-aliases`).then(r => r.ok ? r.json() : {}),
    ]).then(([p, t, a]) => {
      if (!alive) return;
      setPilots(Array.isArray(p) ? p : []);
      setTeams(Array.isArray(t) ? t : []);
      setAliases(a && typeof a === 'object' ? a : {});
    }).catch(() => { });
    return () => { alive = false; };
  }, []);

  // Polling dispositivi connessi (solo quando il pannello è aperto).
  const refreshDevices = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/race/devices`);
      if (!r.ok) return;
      const j = await r.json();
      // Ordine STABILE per MAC: la posizione di ogni device non cambia tra un
      // polling e l'altro, così si può cambiare l'assegnazione senza che le
      // righe "saltino" (l'ordinamento per età cambiava continuamente).
      const list = (Array.isArray(j.devices) ? j.devices : [])
        .slice()
        .sort((a, b) => String(a.mac).localeCompare(String(b.mac)));
      setDevices(list);
      setLoaded(true);
    } catch { /* no-op */ }
  }, []);
  useEffect(() => {
    if (!expanded) return;
    refreshDevices();
    const t = setInterval(refreshDevices, 2000);
    return () => clearInterval(t);
  }, [expanded, refreshDevices]);

  // Normalizza per ricerca (case + accenti insensibili).
  const normalize = (s = '') => String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

  // Opzioni di assegnazione (piloti + team) ordinate alfabeticamente.
  const options = useMemo(() => {
    const ps = (pilots || []).map(p => ({
      id: String(p.id), type: 'pilot',
      label: `${p.name || ''} ${p.surname || ''}`.trim() || String(p.id),
      surname: String(p.surname || ''), name: String(p.name || ''),
      formula: p.formula?.label || '',
      search: normalize(`${p.name || ''} ${p.surname || ''} ${p.team || ''}`),
    }));
    const ts = (teams || []).map(t => ({
      id: String(t.id), type: 'team',
      label: t.name || String(t.id),
      surname: '', name: String(t.name || ''),
      formula: t.formula?.label || '',
      search: normalize(`${t.name || ''} ${t.description || ''}`),
    }));
    return [...ps, ...ts].sort((a, b) => a.label.localeCompare(b.label));
  }, [pilots, teams]);

  // Filtra le opzioni in base alla query (nome, cognome o team), max 8 risultati.
  const filterOptions = useCallback((q) => {
    const nq = normalize(q);
    if (!nq) return [];
    return options.filter(o => o.search.includes(nq)).slice(0, 8);
  }, [options]);
  const setQ = useCallback((mac, val) => setQuery(s => ({ ...s, [mac]: val })), []);

  const labelForPilot = useCallback((pid) => {
    if (!pid) return null;
    if (String(pid) === FLAG_ROLE) return 'Vettura bandiera gialla';
    const o = options.find(o => o.id === String(pid));
    return o ? o.label : String(pid);
  }, [options]);

  // pilotId -> MAC attualmente assegnato (per avvisare di spostamenti).
  const macByPilot = useMemo(() => {
    const m = {};
    devices.forEach(d => { if (d.assignedPilotId) m[String(d.assignedPilotId)] = d.mac; });
    return m;
  }, [devices]);

  // Stato pilota dalla classifica live (giri/posizione) per pilotId.
  const driverByPilot = useMemo(() => {
    const m = {};
    (drivers || []).forEach(d => { if (d.pilotId) m[String(d.pilotId)] = d; });
    return m;
  }, [drivers]);

  // Mappa id → entità (pilota o team): serve per foto e nome nel riquadro assegnato.
  const entityById = useMemo(() => {
    const m = {};
    (pilots || []).forEach(p => { m[String(p.id)] = { type: 'pilot', obj: p }; });
    (teams || []).forEach(t => { m[String(t.id)] = { type: 'team', obj: t }; });
    return m;
  }, [pilots, teams]);

  // ID del dispositivo dal file device-aliases (MAC→ID). Se presente, in UI
  // mostriamo l'ID al posto del MAC grezzo. null = nessun alias → si mostra il MAC.
  const deviceIdOf = useCallback((mac) => {
    const v = aliases[String(mac).toUpperCase()];
    return (v != null && String(v).trim() !== '') ? String(v).trim() : null;
  }, [aliases]);

  // Risolve l'alias di un MAC (nome) a un pilota suggerito.
  const aliasPilotFor = useCallback((mac) => {
    const alias = aliases[String(mac).toUpperCase()];
    if (!alias) return null;
    const target = String(alias).trim().toLowerCase();
    const o = options.find(o =>
      o.surname.toLowerCase() === target ||
      o.name.toLowerCase() === target ||
      o.label.toLowerCase() === target);
    return o ? { ...o, alias } : { id: null, label: alias, alias };
  }, [aliases, options]);

  const shortMac = (mac) => { const s = String(mac || ''); return s.length > 8 ? '…' + s.slice(-8) : s; };
  // Etichetta breve del dispositivo: "ID n" se c'è un alias, altrimenti MAC corto.
  const deviceLabelShort = useCallback((mac) => {
    const id = deviceIdOf(mac);
    return id ? `ID ${id}` : shortMac(mac);
  }, [deviceIdOf]);
  const fmtAge = (ms) => (ms == null ? '—' : ms < 1500 ? 'live' : `${Math.round(ms / 1000)}s fa`);

  const doAssign = async (mac, pilotId) => {
    setBusyMac(mac); setError('');
    try {
      const r = await fetch(`${API_BASE}/api/race/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac, pilotId: pilotId || null }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setError(j.error || 'Assegnazione fallita');
      await refreshDevices();
    } catch (e) {
      setError(e.message || 'Errore di rete');
    } finally {
      setBusyMac(null);
      setPending(null);
      setQ(mac, '');
    }
  };

  // Richiede conferma costruendo un messaggio che spiega l'effetto.
  const requestAssign = (mac, pilotId, currentPilotId) => {
    if (String(pilotId || '') === String(currentPilotId || '')) return;
    let msg;
    if (!pilotId) {
      msg = `Liberare il dispositivo ${deviceLabelShort(mac)} da ${labelForPilot(currentPilotId)}?`;
    } else if (String(pilotId) === FLAG_ROLE) {
      const parts = [`Usare ${deviceLabelShort(mac)} come vettura bandiera gialla: quando entra in pista la gara passa in YELLOW FLAG automaticamente.`];
      if (currentPilotId) parts.push(`${labelForPilot(currentPilotId)} resterà senza questo dispositivo.`);
      msg = parts.join(' ');
    } else {
      const movingFrom = macByPilot[String(pilotId)];
      const parts = [`Assegnare ${deviceLabelShort(mac)} a ${labelForPilot(pilotId)}.`];
      if (movingFrom && String(movingFrom).toUpperCase() !== String(mac).toUpperCase()) {
        parts.push(`Sarà spostato dal dispositivo ${deviceLabelShort(movingFrom)}.`);
      }
      if (currentPilotId) parts.push(`${labelForPilot(currentPilotId)} resterà senza questo dispositivo.`);
      parts.push('Giri, settori, penalità e storico restano agganciati al pilota.');
      msg = parts.join(' ');
    }
    setPending({ mac, pilotId: pilotId || null, message: msg });
  };

  // Piloti già in gara ma il cui dispositivo non è online (segnale perso /
  // vettura ferma): vanno riassegnati a un device funzionante.
  const onlinePilotIds = useMemo(
    () => new Set(devices.filter(d => d.assignedPilotId).map(d => String(d.assignedPilotId))),
    [devices]
  );
  const driversOffline = useMemo(
    () => (drivers || []).filter(d => d.pilotId && !onlinePilotIds.has(String(d.pilotId))),
    [drivers, onlinePilotIds]
  );

  const assignedCount = devices.filter(d => d.assignedPilotId).length;

  return (
    <section className="rs-card dm-card">
      <div className="rs-card__head rs-card__head--toggle" onClick={onToggle}>
        <span className="rs-card__title"><Icon.Device /> Dispositivi</span>
        <div className="dm-head-right">
          <span className="rs-chip"><Icon.Wifi /> <strong>{devices.length}</strong> online</span>
          <span className="rs-chip"><Icon.Users /> <strong>{assignedCount}</strong> assegnati</span>
          <Icon.ChevronDown className={`rs-chevron ${expanded ? 'rs-chevron--open' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="rs-card__body dm-content">
          <p className="rs-help dm-intro">Assegna o cambia il dispositivo di un pilota a gara in corso: giri, settori, penalità e storico restano agganciati al pilota.</p>
          {error && <div className="rs-alert rs-alert--danger"><Icon.Alert /> <span>{error}</span></div>}

          {driversOffline.length > 0 && (
            <div className="rs-alert rs-alert--warning">
              <Icon.Alert />
              <span>
                <strong>Senza segnale:</strong> {driversOffline.map(d => d.fullName || d.tag).join(', ')}
                {' '}— assegna loro un dispositivo collegato qui sotto.
              </span>
            </div>
          )}

          {!loaded ? (
            <div className="rs-empty"><div className="rs-spinner" /><strong>Caricamento dispositivi…</strong></div>
          ) : devices.length === 0 ? (
            <div className="rs-empty">
              <Icon.Device size={28} />
              <strong>Nessun dispositivo collegato</strong>
              <p>Nessun pacchetto GPS ricevuto negli ultimi 15 secondi.</p>
            </div>
          ) : (
            <div className="dm-grid">
              {devices.map(dev => {
                const cur = dev.assignedPilotId ? String(dev.assignedPilotId) : '';
                const isFlagDev = cur === FLAG_ROLE;
                const flagState = isFlagDev ? (flagDevices.find(f => String(f.mac).toUpperCase() === String(dev.mac).toUpperCase()) || null) : null;
                const live = cur ? driverByPilot[cur] : null;
                const aliasSugg = !cur ? aliasPilotFor(dev.mac) : null;
                const isPending = pending && String(pending.mac).toUpperCase() === String(dev.mac).toUpperCase();
                const busy = busyMac && String(busyMac).toUpperCase() === String(dev.mac).toUpperCase();
                const devId = deviceIdOf(dev.mac);
                const ent = cur ? entityById[cur] : null;
                const isTeam = ent?.type === 'team';
                const eo = ent?.obj || null;
                const photoUrl = eo
                  ? (isTeam
                    ? (eo.logoUrl ? `${API_BASE}${eo.logoUrl}` : TEAM_DEFAULT)
                    : (eo.photoDriverUrl ? `${API_BASE}${eo.photoDriverUrl}` : PILOT_DEFAULT))
                  : null;
                const teamLogoUrl = (eo && !isTeam)
                  ? (eo.photoTeamUrl ? `${API_BASE}${eo.photoTeamUrl}` : null)
                  : null;
                const hasQuery = (query[dev.mac] || '').trim() !== '';
                return (
                  <div
                    key={dev.mac}
                    className={`dm-item ${cur ? 'is-assigned' : 'is-free'} ${isTeam ? 'is-team' : ''} ${isFlagDev ? 'is-flag' : ''} ${isPending ? 'is-pending' : ''}`}
                  >
                    <div className="dm-item__head">
                      <div className="dm-item__id">
                        <span className={`dm-dot ${dev.ageMs < 2000 ? 'is-live' : 'is-stale'}`} />
                        {devId
                          ? <span className="dm-devid" title={dev.mac}><small>ID</small>{devId}</span>
                          : <span className="dm-mac" title={dev.mac}>{dev.mac}</span>}
                        <span className="dm-age">{fmtAge(dev.ageMs)}</span>
                      </div>
                      {isFlagDev
                        ? <span className="rs-badge rs-badge--warning">Bandiera gialla</span>
                        : cur
                          ? <span className={`rs-badge ${isTeam ? 'rs-badge--info' : 'rs-badge--lime'}`}>{isTeam ? 'Team' : 'Pilota'}</span>
                          : <span className="rs-badge">Libero</span>}
                    </div>

                    <div className="dm-item__meta">
                      {dev.battery != null && <span className="rs-chip">BAT <strong>{dev.battery}%</strong></span>}
                      {dev.signal4g != null && <span className="rs-chip">4G <strong>{dev.signal4g}</strong></span>}
                      <span className={`rs-chip ${dev.fix === false ? 'dm-chip--nofix' : ''}`}>SAT <strong>{dev.sats ?? 0}</strong>{dev.fix === false && <em>no fix</em>}</span>
                      <span className="rs-chip"><strong>{Math.round(dev.speedKmh || 0)}</strong> km/h</span>
                      {dev.hr != null
                        ? <span className="rs-chip dm-chip--hr" title={`Fascia cardio #${dev.beaconId ?? '—'}`}><Icon.Heart size={11} /><strong>{dev.hr}</strong> bpm</span>
                        : dev.beaconId
                          ? <span className="rs-chip dm-chip--hr is-idle" title="Sensore agganciato, nessun battito valido"><Icon.Heart size={11} />#{dev.beaconId}</span>
                          : null}
                    </div>

                    {isFlagDev ? (
                      <div className="dm-assigned dm-assigned--flag">
                        <span className="dm-flag__icon"><Icon.Flag size={18} /></span>
                        <div className="dm-assigned__info">
                          <span className="dm-assigned__name">Vettura bandiera gialla</span>
                          <span className="dm-assigned__stat">{flagState?.onTrack ? 'IN PISTA · gialla automatica' : flagState?.seen ? 'fuori pista' : 'in attesa dati…'}</span>
                        </div>
                        <button className="rs-btn rs-btn--icon rs-btn--sm rs-btn--ghost" disabled={busy} title="Rimuovi ruolo" onClick={() => requestAssign(dev.mac, '', cur)}>
                          <Icon.X size={14} />
                        </button>
                      </div>
                    ) : cur ? (
                      <div className="dm-assigned">
                        <div className="dm-assigned__photos">
                          {photoUrl && <img className={`rs-avatar ${isTeam ? 'dm-avatar--team' : ''}`} src={photoUrl} alt={labelForPilot(cur)} />}
                          {teamLogoUrl && <img className="dm-assigned__teamlogo" src={teamLogoUrl} alt="" />}
                        </div>
                        <div className="dm-assigned__info">
                          <span className="dm-assigned__name">{labelForPilot(cur)}</span>
                          <span className="dm-assigned__stat">
                            {live ? `P${live.position ?? '—'} · ${live.lapCount ?? 0} giri` : 'in attesa dati…'}
                          </span>
                        </div>
                        <button className="rs-btn rs-btn--icon rs-btn--sm rs-btn--ghost" disabled={busy} title="Libera dispositivo" onClick={() => requestAssign(dev.mac, '', cur)}>
                          <Icon.X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="dm-assign">
                        {aliasSugg && aliasSugg.id && (
                          <button className="rs-btn rs-btn--lime rs-btn--sm rs-btn--block" disabled={busy} onClick={() => requestAssign(dev.mac, aliasSugg.id, cur)}>
                            Assegna {aliasSugg.label}
                          </button>
                        )}
                        <button className="rs-btn rs-btn--warning rs-btn--sm rs-btn--block" disabled={busy} title="Vettura bandiera gialla (safety car)" onClick={() => requestAssign(dev.mac, FLAG_ROLE, cur)}>
                          <Icon.Flag size={13} /> Vettura bandiera gialla
                        </button>
                        <div className="rs-search dm-search">
                          <Icon.Search />
                          <input
                            type="text"
                            className="rs-input"
                            placeholder="Cerca pilota o team…"
                            value={query[dev.mac] || ''}
                            disabled={busy}
                            onChange={(e) => setQ(dev.mac, e.target.value)}
                          />
                          {hasQuery && (
                            <div className="dm-results">
                              {filterOptions(query[dev.mac]).length === 0 ? (
                                <div className="dm-results__empty">Nessun risultato</div>
                              ) : (
                                filterOptions(query[dev.mac]).map(o => {
                                  const onMac = macByPilot[o.id];
                                  const elsewhere = onMac && String(onMac).toUpperCase() !== String(dev.mac).toUpperCase();
                                  const isCurrent = String(o.id) === cur;
                                  return (
                                    <button
                                      key={o.id}
                                      className={`dm-results__item ${isCurrent ? 'is-current' : ''}`}
                                      disabled={busy || isCurrent}
                                      onClick={() => { setQ(dev.mac, ''); requestAssign(dev.mac, o.id, cur); }}
                                    >
                                      <span className="dm-results__main">
                                        {o.type === 'team' && <span className="rs-badge rs-badge--info">Team</span>}
                                        {o.label}
                                        {o.formula ? <span className="dm-results__sub"> · {o.formula}</span> : null}
                                      </span>
                                      {isCurrent
                                        ? <span className="rs-badge rs-badge--lime">attuale</span>
                                        : elsewhere
                                          ? <span className="rs-badge rs-badge--warning">su {deviceLabelShort(onMac)}</span>
                                          : null}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {isPending && (
                      <div className="rs-confirm rs-confirm--warning dm-confirm">
                        <p className="rs-confirm__text">{pending.message}</p>
                        <div className="rs-confirm__actions">
                          <button className="rs-btn rs-btn--ghost rs-btn--sm" disabled={busy} onClick={() => setPending(null)}>Annulla</button>
                          <button className="rs-btn rs-btn--primary rs-btn--sm" disabled={busy} onClick={() => doAssign(pending.mac, pending.pilotId)}>
                            {busy ? 'Applico…' : 'Conferma'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Interruttore "9:16" accanto a ogni overlay OBS: apre la versione verticale
// (dirette Instagram / YouTube).
function ObsFormatToggle({ on, onToggle, title }) {
  return (
    <button
      type="button"
      className={`lv-obs__fmt ${on ? 'is-on' : ''}`}
      aria-pressed={on}
      aria-label={`${title}: formato verticale 9:16`}
      title={on ? 'Verticale 9:16 (Instagram / YouTube) — clic per tornare a 16:9' : 'Orizzontale 16:9 — clic per il formato verticale 9:16 (Instagram / YouTube)'}
      onClick={onToggle}
    >
      <Icon.Device size={13} />
      <span>9:16</span>
    </button>
  );
}

export default function RaceLive({ raceConfig = {}, onStopRace = () => { }, overlayMode, publicMode = false, raceId }) {
  const { circuitData, totalLaps } = raceConfig || {};
  const navigate = useNavigate();
  const location = useLocation();
  const isOverlayMap = overlayMode === 'map';
  const isOverlayLeaderboard = overlayMode === 'leaderboard';
  // Overlay verticale 9:16 (?format=vertical) per dirette Instagram / YouTube
  const overlayParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const overlayVertical = !!overlayMode && isVerticalFormat(overlayParams);
  // Overlay OBS su green screen: nessun colore verde nei contenuti, altrimenti il
  // chroma key lo renderebbe trasparente (piloti, settori, numeri curva).
  const paintRef = useRef(overlayMode ? chromaSafeColor : (c) => c);

  // Chiave canonica del driver = identità STABILE del pilota (pilotId).
  // NON usare il MAC: a gara in corso un pilota può cambiare dispositivo
  // (vettura rotta) e il MAC cambia, ma giri/settori/penalità/storico restano
  // agganciati al pilota. Tutti gli stati lato client (telemetria, trail, colori,
  // smoothing, fasi DR, classifica) sono indicizzati con questa chiave.
  const keyOf = (d) => (d && (d.id || d.pilotId)) || (d && d.mac) || '';

  const [snapshot, setSnapshot] = useState(null);
  const [initCircuit, setInitCircuit] = useState(null);
  const [blink, setBlink] = useState(true);
  const [penTargetId, setPenTargetMac] = useState('');
  const [penaltyReason, setPenaltyReason] = useState('');
  const [yellowFlagViolations, setYellowFlagViolations] = useState([]);
  const [obsSelections, setObsSelections] = useState({
    map: false, leaderboard: false,
    'graphics': false, 'graphics/status': false, 'graphics/gaps': false, 'graphics/fastest': false, 'graphics/battle': false,
    'graphics/topspeed': false, 'graphics/sectors': false, 'graphics/podium': false, 'graphics/driver': false, 'graphics/heart': false,
    'sponsors': false, 'sponsors/banner': false, 'sponsors/ticker': false,
    'social/instagram': false, 'social/youtube': false, 'social/youtube-verticale': false,
  });
  // Formato verticale 9:16 (dirette Instagram / YouTube) scelto overlay per overlay;
  // ricordato nel browser della regia.
  const [obsVertical, setObsVertical] = useState(loadObsVertical);
  const toggleObsVertical = (key) => {
    setObsVertical(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveObsVertical(next);
      return next;
    });
  };
  const [sponsors, setSponsors] = useState([]); // sponsor attivi, per gli overlay OBS sponsor
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPenaltyConfirm, setShowPenaltyConfirm] = useState(null); // { type, label }
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  // ⬇️ NUOVO: selezione tipologia classifica
  const [selectedFormulaId, setSelectedFormulaId] = useState('ALL');
  const [exportSelections, setExportSelections] = useState({ ALL: true });

  // Sponsor attivi per il pannello OBS (solo modalità operatore, non overlay/pubblico)
  useEffect(() => {
    if (isOverlayMap || isOverlayLeaderboard || publicMode) return undefined;
    let alive = true;
    fetch(`${API_BASE}/api/sponsors`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(j => { if (alive) setSponsors((j?.sponsors || []).filter(s => s.active !== false && s.logoUrl)); })
      .catch(() => { /* opzionale: nessuno sponsor */ });
    return () => { alive = false; };
  }, [isOverlayMap, isOverlayLeaderboard, publicMode]);

  // === F1-style overtake animations
  const prevPositionsRef = useRef(new Map());
  const [overtakeAnimations, setOvertakeAnimations] = useState(new Map());

  // === Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    map: true,
    leaderboard: true,
    detailedLeaderboard: true,
    obs: !publicMode,
    export: !publicMode,
    share: !publicMode,
    raceControl: !publicMode,
    devices: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // === telemetria live
  const telemRef = useRef(new Map());
  const lastTelemTsRef = useRef(0);
  useEffect(() => { const t = setInterval(() => setBlink(b => !b), 700); return () => clearInterval(t); }, []);

  // trails + colori
  const trailsRef = useRef({});
  const TRAIL_MAX_AGE_MS = 2000;
  const TRAIL_MAX_LEN = 15;
  const TRAIL_MIN_MOVE_M = 2.5;

  const addTrailPoints = (arr) => {
    const now = Date.now();
    (arr || []).forEach(d => {
      if (!d || !d.key || !Number.isFinite(d.lat) || !Number.isFinite(d.lon)) return;
      const t = trailsRef.current[d.key] || [];
      const last = t[t.length - 1];
      if (!last || haversine(last.lat, last.lon, d.lat, d.lon) >= TRAIL_MIN_MOVE_M) {
        const next = [...t, { lat: d.lat, lon: d.lon, ts: now }];
        trailsRef.current[d.key] = next.length > TRAIL_MAX_LEN ? next.slice(-TRAIL_MAX_LEN) : next;
      }
    });
  };

  const colorsRef = useRef({});

  // smoothing
  const renderPosRef = useRef(new Map());
  const lastAnimTsRef = useRef(performance.now());
  const SMOOTHING_BASE = 0.18;
  const SMOOTHING_MAX_STEP_MS = 50;
  const EPS_METERS = 0.2;

  // Fetch-once della geometria completa del circuito (bordi/cordoli/curve),
  // che gli init payload non includono. Chiave: circuit id già caricato.
  const geomFetchedRef = useRef(null);

  // === DEAD RECKONING ===
  // Map<mac, { phase, lastSeenMs, alpha }>
  const drPhaseRef = useRef(new Map());
  // Vetture "bandiera gialla" (posizioni dal frame telemetry 15 Hz)
  const flagTelemRef = useRef([]);
  // Alert per direzione gara (solo se !publicMode). Array di { level, mac, fullName, lastSeenMs, ... }
  const [directionAlerts, setDirectionAlerts] = useState([]);

  // canvas & interaction refs
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const dirtyRef = useRef(false);
  const lastDrawRef = useRef(0);
  const pointersRef = useRef(new Map());
  const pinchStartRef = useRef({ dist: 0, zoom: 1 });
  const rotationSequenceRef = useRef([]);

  // websocket
  useEffect(() => {
    let cancelled = false;
    let ws = null;
    let reconnectTimer = null;

    const handleMessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.type === 'race_init') {
          // non sovrascrivere un circuito che ha già la geometria completa (bordi/cordoli)
          setInitCircuit(prev => (prev && Array.isArray(prev.leftBorder) && prev.leftBorder.length > 2) ? prev : (data.circuit || null));
          setSnapshot(s => s ? { ...s, totalLaps: data.totalLaps, raceStatus: data.raceStatus } : s);
          return;
        }

        if (data?.type === 'race_snapshot') {
          // colori stabili (indicizzati per identità pilota, non per MAC)
          (data.drivers || []).forEach(d => {
            const k = keyOf(d);
            const keyName = d.fullName || d.tag || d.mac;
            if (k && !colorsRef.current[k]) {
              colorsRef.current[k] = paintRef.current(colorFromName(keyName));
            }
          });

          addTrailPoints((data.drivers || []).map(d => ({ key: keyOf(d), lat: d.lat, lon: d.lon })));

          if (data.circuit?.id && geomFetchedRef.current !== data.circuit.id) {
            geomFetchedRef.current = data.circuit.id;
            try {
              const rc = await fetch(`${API_BASE}/api/circuits/${data.circuit.id}`);
              if (rc.ok) {
                const full = await rc.json();
                setInitCircuit(prev => ({ ...(prev || {}), id: data.circuit.id, name: full.name || data.circuit.id, stats: full.stats || {}, params: full.params || {}, sectors: full.sectors || [], customSectors: full.customSectors || [], leftBorder: full.leftBorder || [], rightBorder: full.rightBorder || [], kerbs: full.kerbs || [], corners: full.corners || [] }));
              }
            } catch { /* no-op */ }
          }

          console.log('[RaceLive] Snapshot ricevuto:', {
            totalLaps: data.totalLaps,
            maxYellowFlagSpeed: data.maxYellowFlagSpeed,
            violations: data.yellowFlagViolations?.length || 0
          });
          setSnapshot(data);
          setYellowFlagViolations(data.yellowFlagViolations || []);
          dirtyRef.current = true;
          return;
        }

        if (data?.type === 'telemetry' && Array.isArray(data.drivers)) {
          const now = Date.now();
          lastTelemTsRef.current = now;

          // Track delle identità presenti nel frame: quelle che NON ci sono più
          // sono GONE e vanno rimosse da telemRef per far scomparire il marker.
          const presentInFrame = new Set();

          data.drivers.forEach(d => {
            const k = keyOf(d);
            if (!k) return;
            presentInFrame.add(k);
            if (!Number.isFinite(d.lat) || !Number.isFinite(d.lon)) return;
            telemRef.current.set(k, { lat: d.lat, lon: d.lon, speedKmh: d.speedKmh || 0, ts: data.ts || now });

            // Dead Reckoning state per-identità pilota
            drPhaseRef.current.set(k, {
              phase: d.drPhase || 'LIVE',
              lastSeenMs: d.lastSeenMs || 0,
              alpha: (d.drAlpha != null) ? d.drAlpha : 1
            });

            if (!colorsRef.current[k]) colorsRef.current[k] = paintRef.current(colorFromName(d.fullName || d.tag || d.mac || k));
          });

          // Driver usciti dal frame (GONE lato server): rimuovi posizione
          // visibile e trail, ma mantieni la fase 'GONE' per l'eventuale alert.
          Array.from(telemRef.current.keys()).forEach(k => {
            if (!presentInFrame.has(k)) {
              telemRef.current.delete(k);
              trailsRef.current[k] = [];
              drPhaseRef.current.set(k, { phase: 'GONE', lastSeenMs: 12000, alpha: 0 });
            }
          });

          addTrailPoints(data.drivers.map(d => ({ key: keyOf(d), lat: d.lat, lon: d.lon })));

          if (Array.isArray(data.flagDevices)) flagTelemRef.current = data.flagDevices;

          // Alert direzione gara: SOLO se non siamo in modalità pubblica.
          if (!publicMode && Array.isArray(data.directionAlerts)) {
            setDirectionAlerts(data.directionAlerts);
          }

          dirtyRef.current = true;
          return;
        }

        if (data?.type === 'race_inactive') {
          setSnapshot(null);
          telemRef.current.clear();
          trailsRef.current = {};
          dirtyRef.current = true;
          return;
        }
      } catch { /* ignore */ }
    };

    // Connessione con RICONNESSIONE AUTOMATICA: fondamentale per gli overlay
    // OBS, che devono restare vivi tra una gara e l'altra (cambio gara, riavvio
    // del server, timeout idle del proxy) SENZA dover ricaricare la sorgente
    // browser. Alla (ri)connessione il server reinvia subito initPayload +
    // snapshot della gara corrente (o race_inactive), quindi l'overlay riparte
    // da solo appena parte una nuova gara.
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(publicMode ? WS_URL + '?role=viewer' : WS_URL);
      ws.onmessage = handleMessage;
      ws.onerror = (e) => console.error('[RaceLive] WS error', e);
      ws.onclose = () => {
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { }
    };
  }, [initCircuit?.sectors?.length]);

  // Colori settori
  const sectorColors = useMemo(() => {
    const circuit = circuitData || initCircuit || snapshot?.circuit || {};
    const custom = circuit?.customSectors || [];
    const fallback = ['#ff4d4f', '#2ecc71', '#3498db'];
    const out = [0, 1, 2].map(i => custom[i]?.color || fallback[i]);
    return out.map(c => paintRef.current(c));
  }, [circuitData, initCircuit, snapshot?.circuit]);

  // === NUOVO: lista formule presenti nello snapshot
  const availableFormulas = useMemo(() => {
    const map = new Map(); // id -> label
    (snapshot?.drivers || []).forEach(d => {
      const id = d?.formula?.id;
      const label = d?.formula?.label;
      if (id && label) map.set(id, label);
    });
    // ordina alfabetico per label
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [snapshot?.drivers]);

  const rankingIds = useMemo(() => ['ALL', ...availableFormulas.map(([id]) => id)], [availableFormulas]);

  useEffect(() => {
    setExportSelections(prev => {
      const next = { ...prev };
      let changed = false;

      rankingIds.forEach(id => {
        if (!(id in next)) {
          next[id] = true;
          changed = true;
        }
      });

      Object.keys(next).forEach(id => {
        if (!rankingIds.includes(id)) {
          delete next[id];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [rankingIds]);

  const formulaLabelMap = useMemo(() => new Map(availableFormulas), [availableFormulas]);

  const driverCounts = useMemo(() => {
    const base = snapshot?.drivers || [];
    return rankingIds.reduce((acc, id) => {
      acc[id] = base.filter(d => (id === 'ALL' ? true : d?.formula?.id === id)).length;
      return acc;
    }, {});
  }, [rankingIds, snapshot?.drivers]);

  const rankingOptions = useMemo(() => rankingIds.map(id => ({
    id,
    label: id === 'ALL' ? 'Classifica generale' : (formulaLabelMap.get(id) || id),
    count: driverCounts[id] || 0,
  })), [driverCounts, formulaLabelMap, rankingIds]);

  const selectedRankingIds = rankingOptions.filter(opt => exportSelections[opt.id]).map(opt => opt.id);

  useEffect(() => {
    rotationSequenceRef.current = availableFormulas.map(([id]) => id);
  }, [availableFormulas]);

  useEffect(() => {
    if (!isOverlayLeaderboard) return undefined;
    let idx = 0;
    const resolveSequence = () => {
      const seq = ['ALL', ...rotationSequenceRef.current];
      return seq.length ? seq : ['ALL'];
    };
    let currentSeq = resolveSequence();
    const tick = () => {
      currentSeq = resolveSequence();
      setSelectedFormulaId(currentSeq[idx % currentSeq.length]);
      idx = (idx + 1) % currentSeq.length;
    };
    tick();
    const timer = setInterval(tick, 10000);
    return () => clearInterval(timer);
  }, [isOverlayLeaderboard]);

  // === NUOVO: leaderboard filtrato
  const leaderboard = useMemo(() => {
    const src = (snapshot?.drivers || []).filter(d => {
      if (selectedFormulaId === 'ALL') return true;
      return d?.formula?.id === selectedFormulaId;
    });
    return src.length ? [...src].sort((a, b) => a.position - b.position) : [];
  }, [snapshot?.drivers, selectedFormulaId]);

  const globalBestLap = useMemo(() => {
    const laps = (snapshot?.drivers || [])
      .filter(d => selectedFormulaId === 'ALL' ? true : d?.formula?.id === selectedFormulaId)
      .map(d => d.bestLapTime)
      .filter(v => Number.isFinite(v));
    return laps.length ? Math.min(...laps) : null;
  }, [snapshot?.drivers, selectedFormulaId]);

  // === Detect overtakes and trigger animations (F1 style)
  useEffect(() => {
    if (!leaderboard.length) return;

    const newAnimations = new Map();

    leaderboard.forEach((driver, currentIdx) => {
      const dk = keyOf(driver);
      const prevPos = prevPositionsRef.current.get(dk);

      if (prevPos !== undefined && prevPos !== currentIdx) {
        // Position changed! Detect if it's an overtake (improvement) or being overtaken
        if (currentIdx < prevPos) {
          // Driver improved position (overtook someone)
          newAnimations.set(dk, { type: 'up', from: prevPos, to: currentIdx });
        } else {
          // Driver lost position (was overtaken)
          newAnimations.set(dk, { type: 'down', from: prevPos, to: currentIdx });
        }
      }

      prevPositionsRef.current.set(dk, currentIdx);
    });

    if (newAnimations.size > 0) {
      setOvertakeAnimations(newAnimations);
      // Clear animations after animation duration
      const timer = setTimeout(() => {
        setOvertakeAnimations(new Map());
      }, 1200); // Match CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [leaderboard]);

  // ---- canvas draw (identico, usa snapshot filtrato solo per liste UI; il disegno pista rimane generale)
  useEffect(() => {
    const circuit = circuitData || initCircuit;
    if (!circuit || !canvasRef.current || !circuit?.sectors?.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: true });
    const dpr = window.devicePixelRatio || 1;
    // Mappa overlay verticale 9:16: il disegno usa coordinate logiche larghe 540
    // (come le altre grafiche verticali), così scritte e pallini crescono con la
    // Browser Source invece di restare minuscoli a 1080x1920.
    const vParams = new URLSearchParams(window.location.search);
    const vMap = isOverlayMap && isVerticalFormat(vParams);
    // in verticale il circuito sta nella fascia bassa, sopra i commenti
    const vBox = stageBox(vParams, verticalPos(vParams.get('pos')), 'map');
    let k = dpr; // pixel del backing store per unità logica di disegno

    canvas.style.touchAction = 'none';
    const ensureSize = () => {
      // Use the canvas's own rendered size (via getBoundingClientRect) so the
      // internal coordinate system always matches the actual displayed box,
      // regardless of parent flex layout (fixes mobile stretching).
      const rect = canvas.getBoundingClientRect();
      const w = rect.width || canvas.parentElement.clientWidth || 600;
      const h = rect.height || canvas.parentElement.clientHeight || 520;
      const nextK = vMap ? dpr * Math.max(1, Math.min(w / V_W, h / V_H)) : dpr;
      if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr) || nextK !== k) {
        canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
        k = nextK;
        ctx.setTransform(k, 0, 0, k, 0, 0);
      }
    };
    const onResize = () => ensureSize();
    ensureSize();
    window.addEventListener('resize', onResize);
    // Observe the canvas box directly so flex/media-query changes immediately
    // resize the backing store to match the displayed box (no stretching).
    let ro = null;
    let resizeRaf = null;
    const scheduleEnsureSize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        ensureSize();
      });
    };
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(scheduleEnsureSize);
      ro.observe(canvas);
    }

    // proiezione
    const lats = circuit.sectors.map(s => s.lat);
    const lons = circuit.sectors.map(s => s.lon);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const centerLat = (minLat + maxLat) / 2, centerLon = (minLon + maxLon) / 2;
    const R = 6371000, centerLatRad = centerLat * Math.PI / 180;
    const dxList = lons.map(l => (l - centerLon) * Math.PI / 180 * R * Math.cos(centerLatRad));
    const dyList = lats.map(l => (l - centerLat) * Math.PI / 180 * R);
    const maxDx = Math.max(...dxList.map(Math.abs));
    const maxDy = Math.max(...dyList.map(Math.abs));
    const padding = 40;
    // In verticale un circuito più largo che alto viene ruotato di 90° (senza
    // specchiarlo) per sfruttare l'altezza dello schermo.
    const vRotate = vMap && maxDx > maxDy * 1.15;
    const vRx = Math.max(1, (vRotate ? maxDy : maxDx) * 1.1);
    const vRy = Math.max(1, (vRotate ? maxDx : maxDy) * 1.1);

    const project = (lat, lon) => {
      const w = canvas.width / k, h = canvas.height / k;
      if (vMap) {
        const top = h * vBox.top / V_H, bottom = h * vBox.bottom / V_H, side = 22;
        const dx = (lon - centerLon) * Math.PI / 180 * R * Math.cos(centerLatRad);
        const dy = (lat - centerLat) * Math.PI / 180 * R;
        const X = vRotate ? -dy : dx, Y = vRotate ? dx : dy;
        const scale = Math.max(1e-6, Math.min((w - side * 2) / (vRx * 2), (h - top - bottom) / (vRy * 2)));
        return { x: w / 2 + X * scale, y: top + (h - top - bottom) / 2 - Y * scale, scale };
      }
      const maxRange = Math.max(maxDx, maxDy) * 1.1;
      const scale = Math.min(w - padding * 2, h - padding * 2) / (maxRange * 2) * zoomRef.current;
      const dx = (lon - centerLon) * Math.PI / 180 * R * Math.cos(centerLatRad);
      const dy = (lat - centerLat) * Math.PI / 180 * R;
      return { x: w / 2 + dx * scale + panRef.current.x, y: h / 2 - dy * scale + panRef.current.y, scale };
    };

    // interazione (disabilitata in modalità overlay map)
    const wheel = (e) => {
      if (isOverlayMap) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const W = rect.width, H = rect.height;
      const oldZoom = zoomRef.current;
      const dir = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.5, Math.min(80, oldZoom * dir));
      const ratio = newZoom / oldZoom;
      panRef.current.x = panRef.current.x * ratio + (mx - W / 2) * (1 - ratio);
      panRef.current.y = panRef.current.y * ratio + (my - H / 2) * (1 - ratio);
      zoomRef.current = newZoom;
    };
    const mousedown = (e) => { if (isOverlayMap) return; isDraggingRef.current = true; lastMouseRef.current = { x: e.clientX, y: e.clientY }; canvas.style.cursor = 'grabbing'; };
    const mousemove = (e) => { if (isOverlayMap || !isDraggingRef.current) return; panRef.current.x += e.clientX - lastMouseRef.current.x; panRef.current.y += e.clientY - lastMouseRef.current.y; lastMouseRef.current = { x: e.clientX, y: e.clientY }; };
    const mouseup = () => { if (isOverlayMap) return; isDraggingRef.current = false; canvas.style.cursor = 'grab'; };

    if (!isOverlayMap) {
      canvas.addEventListener('wheel', wheel, { passive: false });
      canvas.addEventListener('mousedown', mousedown);
      canvas.addEventListener('mousemove', mousemove);
      canvas.addEventListener('mouseup', mouseup);
      canvas.addEventListener('mouseleave', mouseup);
      canvas.style.cursor = 'grab';
    }

    const onPointerDown = (ev) => {
      if (isOverlayMap) return;
      canvas.setPointerCapture?.(ev.pointerId);
      pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointersRef.current.size === 1) {
        lastMouseRef.current = { x: ev.clientX, y: ev.clientY };
      } else if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        pinchStartRef.current = { dist: Math.hypot(dx, dy), zoom: zoomRef.current };
      }
    };
    const onPointerMove = (ev) => {
      if (isOverlayMap || !pointersRef.current.has(ev.pointerId)) return;
      pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

      if (pointersRef.current.size === 1) {
        const p = pointersRef.current.get(ev.pointerId);
        panRef.current.x += p.x - lastMouseRef.current.x;
        panRef.current.y += p.y - lastMouseRef.current.y;
        lastMouseRef.current = { ...p };
      } else if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        const dist = Math.hypot(dx, dy);
        if (pinchStartRef.current.dist > 0) {
          const oldZoom = zoomRef.current;
          const newZoom = Math.max(0.5, Math.min(80, pinchStartRef.current.zoom * (dist / pinchStartRef.current.dist)));
          const rect = canvas.getBoundingClientRect();
          const mx = (pts[0].x + pts[1].x) / 2 - rect.left;
          const my = (pts[0].y + pts[1].y) / 2 - rect.top;
          const W = rect.width, H = rect.height;
          const zr = newZoom / oldZoom;
          panRef.current.x = panRef.current.x * zr + (mx - W / 2) * (1 - zr);
          panRef.current.y = panRef.current.y * zr + (my - H / 2) * (1 - zr);
          zoomRef.current = newZoom;
        }
      }
      ev.preventDefault();
    };
    const onPointerUp = (ev) => {
      if (isOverlayMap) return;
      canvas.releasePointerCapture?.(ev.pointerId);
      pointersRef.current.delete(ev.pointerId);
      if (pointersRef.current.size < 2) pinchStartRef.current.dist = 0;
    };

    if (!isOverlayMap) {
      canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
      canvas.addEventListener('pointermove', onPointerMove, { passive: false });
      canvas.addEventListener('pointerup', onPointerUp);
      canvas.addEventListener('pointercancel', onPointerUp);
      canvas.addEventListener('pointerleave', onPointerUp);
    }

    const draw = () => {
      const noww = performance.now();
      if (!dirtyRef.current && noww - lastDrawRef.current < 33) return;
      dirtyRef.current = false; lastDrawRef.current = noww;

      ensureSize();
      const w = canvas.width / k, h = canvas.height / k;

      const TC = getCanvasColors();
      if (isOverlayMap) {
        // green screen: asfalto e bordi opachi, altrimenti si tingono del verde di fondo
        TC.track = '#50545a'; TC.outline = '#2c2f35'; TC.textShadow = '#000000';
      }
      ctx.clearRect(0, 0, w, h);
      if (!isOverlayMap) {
        ctx.fillStyle = TC.bg;
        ctx.fillRect(0, 0, w, h);
      } else {
        // In modalità overlay, canvas trasparente
        ctx.globalCompositeOperation = 'source-over';
      }

      const widthMeters = circuit.params?.widthMeters ?? 6;
      const pts = circuit.sectors.map(s => project(s.lat, s.lon));
      const scale = pts[0]?.scale || 1;
      const trackPx = Math.max(6, widthMeters * scale);

      const customSec = circuit.customSectors || circuitData?.customSectors || initCircuit?.customSectors || [];
      const lB = circuit.leftBorder, rB = circuit.rightBorder;
      const hasManualBorders = Array.isArray(lB) && lB.length > 2 && Array.isArray(rB) && rB.length > 2;

      if (hasManualBorders) {
        // === Circuito MANUALE: asfalto = banda piena fra i due bordi (larghezza reale) ===
        const lp = lB.map(p => project(p.lat, p.lon));
        const rp = rB.map(p => project(p.lat, p.lon));
        ctx.beginPath();
        lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
        for (let i = rp.length - 1; i >= 0; i--) ctx.lineTo(rp[i].x, rp[i].y);
        ctx.closePath();
        ctx.fillStyle = TC.track; ctx.fill();
        ctx.strokeStyle = TC.outline; ctx.lineWidth = 1.5;
        ctx.beginPath(); lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
        ctx.beginPath(); rp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
      } else {
        // === LEGACY (circuiti senza bordi): settori + asfalto spessi sulla centerline ===
        if (Array.isArray(customSec) && customSec.length > 0) {
          customSec.forEach((sector, idx) => {
            if (sector.startIdx == null || sector.endIdx == null) return;
            const start = Math.max(0, Math.min(sector.startIdx, circuit.sectors.length - 1));
            const end = Math.max(0, Math.min(sector.endIdx, circuit.sectors.length - 1));
            if (end <= start) return;
            const color = paintRef.current(sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3]);
            ctx.save();
            ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = color; ctx.lineWidth = trackPx + 3; ctx.globalAlpha = 0.65;
            ctx.beginPath();
            for (let i = start; i <= end; i++) {
              const p = project(circuit.sectors[i].lat, circuit.sectors[i].lon);
              if (i === start) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke(); ctx.restore();
          });
        } else {
          ctx.strokeStyle = TC.outline; ctx.lineWidth = trackPx + 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
          ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
        }
        ctx.strokeStyle = TC.track; ctx.lineWidth = trackPx; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
      }
      // Cordoli (linee rosso/bianco, sottili)
      if (Array.isArray(circuit.kerbs)) {
        circuit.kerbs.forEach(k => {
          const kp = (k.points || []).map(p => project(p.lat, p.lon));
          if (kp.length < 2) return;
          const seq = k.closed ? [...kp, kp[0]] : kp;
          const kw = Math.max(2, trackPx * 0.12);
          for (let i = 1; i < seq.length; i++) {
            ctx.beginPath(); ctx.moveTo(seq[i - 1].x, seq[i - 1].y); ctx.lineTo(seq[i].x, seq[i].y);
            ctx.strokeStyle = i % 2 === 0 ? '#e10600' : '#ffffff'; ctx.lineWidth = kw; ctx.lineCap = 'butt';
            ctx.stroke();
          }
        });
      }
      // Settori: segno colorato trasversale dove FINISCE ogni settore (no colore sulla centerline)
      if (hasManualBorders && Array.isArray(customSec) && customSec.length && circuit.sectors?.length) {
        const n = circuit.sectors.length;
        customSec.forEach((sector, idx) => {
          if (sector.endIdx == null) return;
          const e = Math.max(0, Math.min(sector.endIdx, n - 1));
          const a = circuit.sectors[Math.max(0, e - 1)], b = circuit.sectors[Math.min(n - 1, e + 1)];
          const pe = project(circuit.sectors[e].lat, circuit.sectors[e].lon);
          const pa = project(a.lat, a.lon), pb = project(b.lat, b.lon);
          let tx = pb.x - pa.x, ty = pb.y - pa.y; const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl;
          const nx = -ty, ny = tx;
          const half = Math.max(5, trackPx * 0.55);
          ctx.save();
          ctx.strokeStyle = paintRef.current(sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3]);
          ctx.lineWidth = Math.max(2, Math.min(5, trackPx * 0.09)); ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(pe.x - nx * half, pe.y - ny * half); ctx.lineTo(pe.x + nx * half, pe.y + ny * half); ctx.stroke();
          ctx.restore();
        });
      }
      // Numeri curva FUORI dal tracciato, in prossimità della curva
      if (Array.isArray(circuit.corners) && circuit.corners.length && circuit.sectors?.length && pts.length) {
        const n = circuit.sectors.length;
        let cgx = 0, cgy = 0;
        for (const p of pts) { cgx += p.x; cgy += p.y; }
        cgx /= pts.length; cgy /= pts.length;
        const ordered = [...circuit.corners].sort((A, B) => A.startIdx - B.startIdx);
        ctx.font = 'bold 12px Roboto, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ordered.forEach((c, i) => {
          const mid = (c.startIdx <= c.endIdx) ? Math.floor((c.startIdx + c.endIdx) / 2) : c.startIdx;
          const s = circuit.sectors[Math.max(0, Math.min(n - 1, mid))]; if (!s) return;
          const p = project(s.lat, s.lon);
          let ox = p.x - cgx, oy = p.y - cgy; const ol = Math.hypot(ox, oy) || 1; ox /= ol; oy /= ol;
          const off = Math.max(14, trackPx * 0.6 + 12);
          const lx = p.x + ox * off, ly = p.y + oy * off;
          ctx.beginPath(); ctx.arc(lx, ly, 9, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(10,10,14,0.82)'; ctx.fill();
          ctx.strokeStyle = paintRef.current('#C0FF03'); ctx.lineWidth = 1.5; ctx.stroke();
          ctx.fillStyle = paintRef.current('#C0FF03'); ctx.fillText(String(i + 1), lx, ly + 0.5);
        });
        ctx.textBaseline = 'alphabetic';
      }

      // linea start
      if (pts.length > 1) {
        const s0 = pts[0], s1 = pts[1]; const ang = Math.atan2(s1.y - s0.y, s1.x - s0.x);
        ctx.save(); ctx.translate(s0.x, s0.y); ctx.rotate(ang);
        ctx.strokeStyle = '#ffffff'; ctx.lineWidth = Math.max(2, trackPx * 0.12);
        ctx.beginPath(); ctx.moveTo(0, -trackPx * 0.5); ctx.lineTo(0, trackPx * 0.5); ctx.stroke(); ctx.restore();
      }

      // trails cleanup
      const now = Date.now();
      Object.keys(trailsRef.current).forEach(k => {
        trailsRef.current[k] = (trailsRef.current[k] || []).filter(p => now - p.ts <= TRAIL_MAX_AGE_MS).slice(-TRAIL_MAX_LEN);
      });

      // disegna tutti i piloti (generale): le traiettorie/marker restano globali, la classifica è filtrata
      const snapDrivers = snapshot?.drivers || [];

      const visibleDrivers =
        selectedFormulaId === 'ALL'
          ? snapDrivers
          : snapDrivers.filter(d => d?.formula?.id === selectedFormulaId);

      const sorted = [...visibleDrivers].sort((a, b) => a.position - b.position);

      sorted.forEach((d, i) => {
        const dk = keyOf(d);
        // Dead Reckoning: salta completamente i driver in fase GONE (il marker scompare)
        const drInfo = drPhaseRef.current.get(dk);
        const drPhase = drInfo?.phase || d.drPhase || 'LIVE';
        if (drPhase === 'GONE') return;

        const smoothed = renderPosRef.current.get(dk);
        const live = telemRef.current.get(dk);
        const lat = smoothed?.lat ?? live?.lat ?? d.lat;
        const lon = smoothed?.lon ?? live?.lon ?? d.lon;

        const color = colorsRef.current[dk] || paintRef.current(colorFromName(d.fullName || d.tag || d.mac));

        const trail = trailsRef.current[dk] || [];
        if (trail.length > 1) {
          for (let t = 1; t < trail.length; t++) {
            const prev = project(trail[t - 1].lat, trail[t - 1].lon);
            const cur = project(trail[t].lat, trail[t].lon);
            const ageFrac = Math.min(1, (Date.now() - trail[t].ts) / TRAIL_MAX_AGE_MS);
            const alpha = 0.75 * (1 - ageFrac);
            let rgba = color;
            if (color.startsWith('#')) {
              const hex = color.slice(1);
              const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
              const v = parseInt(full, 16), r = (v >> 16) & 255, g = (v >> 8) & 255, b = v & 255;
              rgba = `rgba(${r},${g},${b},${alpha})`;
            }
            ctx.strokeStyle = rgba; ctx.lineWidth = Math.max(2, trackPx * 0.12);
            ctx.beginPath(); ctx.moveTo(prev.x, prev.y); ctx.lineTo(cur.x, cur.y); ctx.stroke();
          }
        }

        const p = project(lat, lon);
        const r = Math.min(Math.max(3, trackPx * 0.10), 11) + (i === 0 ? 1 : 0);
        let fill = color;
        if (fill.startsWith('#')) {
          const hex = fill.slice(1);
          const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
          const v = parseInt(full, 16), rr = (v >> 16) & 255, gg = (v >> 8) & 255, bb = v & 255;
          fill = `rgba(${rr},${gg},${bb},1)`;
        }
        // Pit halo: cerchio arancione pulsante attorno al pilota ai box
        if (d.inPit) {
          ctx.save();
          ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)';
          ctx.lineWidth = 2.5;
          ctx.setLineDash([4, 3]);
          ctx.beginPath(); ctx.arc(p.x, p.y, r + 5, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }

        // === DR phase visual styling ===
        const drAlphaBase = (drInfo?.alpha != null) ? drInfo.alpha : 1;
        let phaseAlpha = 1;
        if (drPhase === 'PREDICT') phaseAlpha = 0.95;
        else if (drPhase === 'COAST') phaseAlpha = 0.80;
        else if (drPhase === 'MODEL') phaseAlpha = 0.70;
        else if (drPhase === 'FADEOUT') phaseAlpha = Math.max(0.15, drAlphaBase);

        // Ring di warning pulsante per fasi avanzate
        if (drPhase === 'COAST' || drPhase === 'MODEL' || drPhase === 'FADEOUT') {
          const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 280);
          const ringColor = drPhase === 'COAST'
            ? `rgba(245, 158, 11, ${0.45 + pulse * 0.25})`
            : drPhase === 'MODEL'
              ? `rgba(250, 204, 21, ${0.55 + pulse * 0.30})`
              : `rgba(239, 68, 68, ${(0.55 + pulse * 0.35) * phaseAlpha})`;
          ctx.save();
          ctx.strokeStyle = ringColor;
          ctx.lineWidth = 2.5;
          ctx.setLineDash([5, 4]);
          ctx.beginPath(); ctx.arc(p.x, p.y, r + 6 + pulse * 2, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }

        ctx.save();
        ctx.globalAlpha = phaseAlpha;
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 2;
        ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0; ctx.strokeStyle = d.inPit ? '#f59e0b' : '#fff'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = TC.text;
        ctx.strokeStyle = TC.textShadow;
        ctx.lineWidth = 2.5;
        ctx.font = 'bold 10px Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.strokeText(d.tag, p.x, p.y - (r + 8));
        ctx.fillText(d.tag, p.x, p.y - (r + 8));
        if (d.inPit) {
          ctx.font = 'bold 7px Roboto, sans-serif';
          ctx.fillStyle = '#f59e0b';
          ctx.fillText('PIT', p.x, p.y + r + 10);
        }
        ctx.restore();
      });

      // Vetture "bandiera gialla": rombo giallo con etichetta; alone pulsante quando è in pista
      (flagTelemRef.current || []).forEach(f => {
        if (!Number.isFinite(f.lat) || !Number.isFinite(f.lon)) return;
        const p = project(f.lat, f.lon);
        const r = Math.min(Math.max(4, trackPx * 0.12), 12);
        ctx.save();
        if (f.onTrack) {
          const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 220);
          ctx.strokeStyle = `rgba(241, 196, 15, ${0.35 + pulse * 0.45})`;
          ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(p.x, p.y, r + 7 + pulse * 3, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.translate(p.x, p.y); ctx.rotate(Math.PI / 4);
        ctx.fillStyle = '#f1c40f'; ctx.strokeStyle = '#111'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.rect(-r, -r, r * 2, r * 2); ctx.fill(); ctx.stroke();
        ctx.restore();
        ctx.save();
        ctx.fillStyle = TC.text; ctx.strokeStyle = TC.textShadow; ctx.lineWidth = 2.5;
        ctx.font = 'bold 10px Roboto, sans-serif'; ctx.textAlign = 'center';
        ctx.strokeText('FLAG', p.x, p.y - (r + 9)); ctx.fillText('FLAG', p.x, p.y - (r + 9));
        ctx.restore();
      });
    };

    const animate = () => {
      const now = performance.now();
      const dt = Math.min(SMOOTHING_MAX_STEP_MS, now - lastAnimTsRef.current);
      lastAnimTsRef.current = now;

      let needRedraw = false;

      const snapDrivers = snapshot?.drivers || [];
      const presentKeys = new Set(snapDrivers.map(keyOf));

      snapDrivers.forEach(d => {
        const dk = keyOf(d);
        const live = telemRef.current.get(dk);
        const targetLat = live?.lat ?? d.lat;
        const targetLon = live?.lon ?? d.lon;

        const prev = renderPosRef.current.get(dk) || { lat: targetLat, lon: targetLon };
        const k = 1 - Math.pow(1 - SMOOTHING_BASE, dt / 16);

        const newLat = prev.lat + (targetLat - prev.lat) * k;
        const newLon = prev.lon + (targetLon - prev.lon) * k;

        if (haversine(prev.lat, prev.lon, newLat, newLon) > EPS_METERS) needRedraw = true;
        renderPosRef.current.set(dk, { lat: newLat, lon: newLon });
      });

      Array.from(renderPosRef.current.keys()).forEach(k => { if (!presentKeys.has(k)) renderPosRef.current.delete(k); });
      if (needRedraw) dirtyRef.current = true;

      const noww = performance.now();
      if (dirtyRef.current || noww - lastDrawRef.current >= 33) draw();

      animFrameRef.current = requestAnimationFrame(animate);
    };

    draw();
    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      if (!isOverlayMap) {
        canvas.removeEventListener('wheel', wheel);
        canvas.removeEventListener('mousedown', mousedown);
        canvas.removeEventListener('mousemove', mousemove);
        canvas.removeEventListener('mouseup', mouseup);
        canvas.removeEventListener('mouseleave', mouseup);
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('pointercancel', onPointerUp);
        canvas.removeEventListener('pointerleave', onPointerUp);
      }
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [circuitData, initCircuit, snapshot, selectedFormulaId, isOverlayMap]);

  const sendStatus = async (status) => {
    try { await fetch(`${API_BASE}/api/race/status`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }); }
    catch (e) { console.error(e); }
  };

  // Avvia la gara effettiva (dopo formation lap)
  const startRaceFromFormation = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/race/formation-start`, { method: 'POST' });
      const result = await response.json();
      if (!response.ok) {
        console.error('Errore avvio gara:', result.error);
        alert('Errore avvio gara: ' + (result.error || 'Errore sconosciuto'));
      }
    } catch (e) {
      console.error('Errore avvio gara:', e);
      alert('Errore avvio gara: ' + e.message);
    }
  };

  const requestPenalty = (type, label) => {
    if (!penTargetId) return;
    setShowPenaltyConfirm({ type, label });
  };

  const sendPenalty = async (type) => {
    if (!penTargetId) return;
    try {
      await fetch(`${API_BASE}/api/race/penalty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pilotId: penTargetId, type, reason: penaltyReason })
      });
      setShowPenaltyConfirm(null);
      setPenaltyReason(''); // reset reason dopo invio
    }
    catch (e) {
      console.error(e);
      setShowPenaltyConfirm(null);
    }
  };

  const toggleObsSelection = (key) => {
    setObsSelections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const acknowledgeViolation = async (violationId) => {
    try {
      await fetch(`${API_BASE}/api/race/violations/${violationId}/acknowledge`, {
        method: 'POST'
      });
      setYellowFlagViolations(prev => prev.filter(v => v.id !== violationId));
    } catch (e) {
      console.error(e);
    }
  };

  // Funzione per scaricare il QR code come PDF
  const downloadQrPdf = useCallback(() => {
    const qrCanvas = document.getElementById('live-share-qr-canvas');
    if (!qrCanvas) return;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Titolo
    pdf.setFontSize(24);
    pdf.setFont(undefined, 'bold');
    pdf.text('RaceSense Live', 105, 30, { align: 'center' });

    // Sottotitolo
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'normal');
    pdf.text('Scansiona per seguire la gara in tempo reale', 105, 42, { align: 'center' });

    // QR Code
    const qrDataUrl = qrCanvas.toDataURL('image/png');
    const qrSize = 100; // mm
    const qrX = (210 - qrSize) / 2; // centrato su A4
    pdf.addImage(qrDataUrl, 'PNG', qrX, 55, qrSize, qrSize);

    // Link sotto il QR
    const publicUrl = `${window.location.origin}/live/current`;
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(publicUrl, 105, 165, { align: 'center' });

    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Powered by RaceSense', 105, 280, { align: 'center' });

    // Download
    const circuitName = snapshot?.circuit?.name || 'race';
    const fileName = `racesense_live_${circuitName.replace(/\s+/g, '_').toLowerCase()}.pdf`;
    pdf.save(fileName);
  }, [snapshot?.circuit?.name]);

  const toggleExportSelection = (id) => {
    setExportSelections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLaunchObsWindows = () => {
    if (typeof window === 'undefined') return;
    const selected = Object.entries(obsSelections).filter(([, enabled]) => enabled).map(([key]) => key);
    if (!selected.length) return;
    selected.forEach((overlayKey, idx) => {
      const url = withFormat(`${window.location.origin}/obs/${overlayKey}`, !!obsVertical[overlayKey]);
      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
      }, idx * 150);
    });
  };

  const circuitName = (circuitData?.name || initCircuit?.name || snapshot?.circuit?.name || 'Circuito');
  const buildRankingList = (id) => {
    const base = (snapshot?.drivers || []).filter(d => (id === 'ALL' ? true : d?.formula?.id === id));
    if (!base.length) return [];
    return [...base].sort((a, b) => {
      const posA = Number.isFinite(a.position) ? a.position : Number.MAX_SAFE_INTEGER;
      const posB = Number.isFinite(b.position) ? b.position : Number.MAX_SAFE_INTEGER;
      return posA - posB;
    });
  };

  const canExportXml = selectedRankingIds.length > 0 && (snapshot?.drivers || []).length > 0;

  const handleExportXml = () => {
    if (!canExportXml || typeof window === 'undefined' || typeof document === 'undefined') return;

    const rankingLabels = new Map(rankingOptions.map(opt => [opt.id, opt.label]));
    const sections = selectedRankingIds.map(id => {
      const drivers = buildRankingList(id);
      if (!drivers.length) return null;
      const label = rankingLabels.get(id) || id;
      const driverNodes = drivers.map(d => {
        const position = Number.isFinite(d.position) ? d.position : '';
        const laps = Number.isFinite(d.lapCount) ? d.lapCount : '';
        const gap = id === 'ALL' ? (d.gapToLeader || '') : (d.classGapToLeader || d.gapToLeader || '');
        const bestLapSeconds = formatSeconds(d.bestLapTime);
        const lastLapSeconds = formatSeconds(d.lastLapTime);
        const bestLapFormatted = Number.isFinite(d.bestLapTime) ? formatLap(d.bestLapTime) : '';
        const lastLapFormatted = Number.isFinite(d.lastLapTime) ? formatLap(d.lastLapTime) : '';
        const formulaLabel = d?.formula?.label || rankingLabels.get(d?.formula?.id) || '';

        return `    <driver position="${xmlEscape(position)}" tag="${xmlEscape(d.tag || '')}" fullName="${xmlEscape(d.fullName || '')}" mac="${xmlEscape(d.mac || '')}" team="${xmlEscape(d.team || '')}" laps="${xmlEscape(laps)}" gap="${xmlEscape(gap)}" status="${xmlEscape(d?.penalty?.dq ? 'DSQ' : (d.inPit ? 'PIT' : ''))}" bestLapSeconds="${xmlEscape(bestLapSeconds)}" bestLapFormatted="${xmlEscape(bestLapFormatted)}" lastLapSeconds="${xmlEscape(lastLapSeconds)}" lastLapFormatted="${xmlEscape(lastLapFormatted)}" formula="${xmlEscape(formulaLabel)}" penalty="${xmlEscape(d?.penalty?.summary || '')}" />`;
      }).join('\n');

      return `  <ranking id="${xmlEscape(id)}" label="${xmlEscape(label)}">\n${driverNodes}\n  </ranking>`;
    }).filter(Boolean);

    if (!sections.length) {
      window.alert('Nessuna classifica disponibile per l\'export.');
      return;
    }

    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<rankings generatedAt="${xmlEscape(new Date().toISOString())}" circuit="${xmlEscape(circuitName)}">\n${sections.join('\n')}\n</rankings>\n`;
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `racesense-classifiche-${Date.now()}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const selectedObsCount = Object.values(obsSelections).filter(Boolean).length;
  const currentStatus = (snapshot?.raceStatus || 'IN CORSO').toUpperCase();
  const isFormationLap = snapshot?.isFormationLap ?? false;
  const remainingSeconds = snapshot?.remainingSeconds ?? null;
  const raceDurationMinutes = snapshot?.raceDurationMinutes ?? null;
  const currentFormulaLabel = selectedFormulaId === 'ALL'
    ? 'GENERALE'
    : (availableFormulas.find(([id]) => id === selectedFormulaId)?.[1] || 'FORMULA');
  const rotationTargetsCount = availableFormulas.length;

  if (isOverlayMap) {
    return (
      <div className="obs-overlay obs-overlay--map">
        <canvas ref={canvasRef} className="track-canvas obs-map-canvas" />
      </div>
    );
  }

  if (isOverlayLeaderboard) {
    const currentLap = leaderboard.length > 0 ? leaderboard[0].lapCount : 0;
    const hasTotal = (snapshot?.totalLaps !== null && snapshot?.totalLaps !== undefined) || (totalLaps !== null && totalLaps !== undefined);
    // Verticale: la classifica sta nella fascia bassa, con le righe che ci entrano
    // (?rows= per forzarle, ?band= per cambiare l'altezza della fascia)
    const vPos = verticalPos(overlayParams.get('pos'));
    const vBox = stageBox(overlayParams, vPos);
    const vFit = Math.max(3, Math.floor((vBox.band - 62) / 32));
    const vRows = Math.max(3, Math.min(20, Number(overlayParams.get('rows')) || vFit));
    const lbRows = overlayVertical ? leaderboard.slice(0, vRows) : leaderboard;
    const hiddenRows = leaderboard.length - lbRows.length;
    const card = (
        <div className="obs-lb">
          <div className="obs-lb__head">
            <span className="obs-lb__live" />
            <span className="obs-lb__title">{currentFormulaLabel}</span>
            <span className="obs-lb__count">{leaderboard.length}</span>
            <span className="obs-lb__lap">LAP {currentLap}{hasTotal ? `/${snapshot?.totalLaps ?? totalLaps}` : ''}</span>
          </div>
          <div className="obs-lb__list">
            {leaderboard.length === 0 ? (
              <div className="obs-lb__empty">In attesa dati GPS…</div>
            ) : (
              lbRows.map((d, idx) => {
                const isLeader = idx === 0;
                const fastest = d.bestLapTime && globalBestLap && d.bestLapTime === globalBestLap;
                const animation = overtakeAnimations.get(keyOf(d));
                const animClass = animation ? `lv-row--${animation.type}` : '';
                const gap = selectedFormulaId === 'ALL' ? d.gapToLeader : (d.classGapToLeader || d.gapToLeader);
                return (
                  <div key={`obs-${keyOf(d)}`} className={`obs-lb__row ${isLeader ? 'is-leader' : ''} ${animClass}`}>
                    <span className={`obs-lb__pos ${idx < 3 ? `is-p${idx + 1}` : ''}`}>{idx + 1}</span>
                    <span className="obs-lb__team">
                      {d.type === 'team' && d.logoUrl
                        ? <img src={`${API_BASE}${d.logoUrl}`} alt={d.team} />
                        : <span className="obs-lb__color" style={{ background: colorsRef.current[keyOf(d)] || paintRef.current(colorFromName(d.fullName || d.tag || d.mac)) }} />}
                    </span>
                    <span className="obs-lb__name">{shortLabel(d)}</span>
                    <span className="obs-lb__icons">
                      {d.inPit && <span className="obs-lb__pit">PIT</span>}
                      {fastest && <span className="obs-lb__fast" title={`Best lap ${formatLap(d.bestLapTime)}`}><Icon.Zap size={11} /></span>}
                    </span>
                    <span className="obs-lb__gap">{isLeader ? 'LEADER' : gap}</span>
                  </div>
                );
              })
            )}
          </div>
          {(rotationTargetsCount > 0 || hiddenRows > 0) && (
            <div className="obs-lb__foot">
              {[rotationTargetsCount > 0 ? `AUTO · ${rotationTargetsCount + 1} CLASSIFICHE` : '', hiddenRows > 0 ? `+${hiddenRows} IN CLASSIFICA` : ''].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
    );
    if (overlayVertical) {
      return (
        <div className="obs-overlay obs-overlay--leaderboard obs-overlay--vertical">
          <VerticalStage box={vBox} className={`ov-stage--${vPos} obs-lb-vstage`}>
            {card}
          </VerticalStage>
        </div>
      );
    }
    return <div className="obs-overlay obs-overlay--leaderboard">{card}</div>;
  }

  const hasTotalLaps = (snapshot?.totalLaps !== null && snapshot?.totalLaps !== undefined) || (totalLaps !== null && totalLaps !== undefined);
  const totalLapsLabel = snapshot?.totalLaps ?? totalLaps;
  const statusSlug = currentStatus.toLowerCase().replace(/\s+/g, '-');
  const penTarget = leaderboard.find(d => d.pilotId === penTargetId);
  const publicUrl = `${window.location.origin}/live/current`;
  const copyPublicLink = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(publicUrl).catch(() => {
        const input = document.querySelector('.lv-share__input');
        input.select();
        document.execCommand('copy');
      });
    } else {
      const input = document.querySelector('.lv-share__input');
      input.select();
      document.execCommand('copy');
    }
  };

  return (
    <div className={`rs-live-root lv ${publicMode ? 'lv--public' : 'lv--operator'}`}>
      {/* === BANNER COLLEGAMENTO RACESENSE PULSE (solo vista pubblica, FORMATION LAP) === */}
      {publicMode && isFormationLap && (
        <PulseLinkBanner drivers={snapshot?.drivers || []} />
      )}

      {/* === OVERLAY ALERT DIREZIONE GARA (Dead Reckoning) — solo direzione gara === */}
      <DirectionAlertsOverlay alerts={directionAlerts} publicMode={publicMode} />

      {/* ═══ TOPBAR LIVE ═══ */}
      <header className="lv-topbar">
        <div className="lv-topbar__left">
          <span className="lv-live"><span className={`lv-live__dot ${blink ? 'on' : ''}`} />LIVE</span>
          <span className={`lv-status lv-status--${statusSlug}`}>
            <RaceFlag status={snapshot?.raceStatus} size={16} />
            <span>{snapshot?.raceStatus || 'IN CORSO'}</span>
          </span>
          {isFormationLap && <span className="rs-badge rs-badge--warning lv-topbar__formation"><Icon.Clock size={12} /> Formation lap</span>}
          {snapshot?.yellowFlagAuto && <span className="rs-badge rs-badge--warning lv-topbar__formation" title="Bandiera gialla attivata automaticamente dalla vettura bandiera"><Icon.Flag size={12} /> Auto · vettura bandiera in pista</span>}
        </div>

        <div className="lv-topbar__center">
          <span className="lv-topbar__item" title="Circuito"><Icon.Track size={14} /><span className="lv-topbar__item-text">{circuitName}</span></span>
          <span className="lv-topbar__sep" />
          {raceDurationMinutes ? (
            <span className={`lv-topbar__item lv-topbar__timer ${remainingSeconds !== null && remainingSeconds < 300 ? 'is-warning' : ''} ${remainingSeconds !== null && remainingSeconds < 60 ? 'is-critical' : ''}`} title="Tempo rimanente">
              <Icon.Clock size={14} />
              <span className="rs-num">{!isFormationLap && remainingSeconds !== null ? formatCountdown(remainingSeconds) : `${raceDurationMinutes}:00`}</span>
            </span>
          ) : (
            <span className="lv-topbar__item" title="Distanza gara"><Icon.Flag size={14} /><span className="rs-num">{hasTotalLaps ? `${totalLapsLabel} GIRI` : 'ENDURANCE'}</span></span>
          )}
          <span className="lv-topbar__sep" />
          <span className="lv-topbar__item" title="Piloti"><Icon.Users size={14} /><span className="rs-num">{leaderboard.length}</span></span>
          {!publicMode && snapshot?.liveViewers != null && (
            <>
              <span className="lv-topbar__sep" />
              <span className="lv-topbar__item" title="Spettatori collegati"><Icon.Eye size={14} /><span className="rs-num">{snapshot.liveViewers}</span></span>
            </>
          )}
        </div>

        <div className="lv-topbar__right">
          <div className="rs-segmented lv-formula">
            <button className={`rs-segmented__item ${selectedFormulaId === 'ALL' ? 'is-active is-red' : ''}`} onClick={() => setSelectedFormulaId('ALL')}>Tutte</button>
            {availableFormulas.map(([fid, label]) => (
              <button key={fid} className={`rs-segmented__item ${selectedFormulaId === fid ? 'is-active is-red' : ''}`} onClick={() => setSelectedFormulaId(fid)}>{label}</button>
            ))}
          </div>
          {!publicMode && (
            <button className="rs-btn rs-btn--sm lv-topbar__settings" onClick={() => setShowSettingsPanel(true)} title="Impostazioni gara — delay e dead reckoning">
              <Icon.Settings size={15} /><span className="rs-hide-mobile">Impostazioni</span>
            </button>
          )}
          <ThemeToggle />
        </div>
      </header>

      {!publicMode && (
        <RaceSettingsPanel
          open={showSettingsPanel}
          onClose={() => setShowSettingsPanel(false)}
        />
      )}

      {/* ═══ BROADCAST STAGE: mappa + classifica overlay ═══ */}
      <section className="lv-stage">
        <canvas ref={canvasRef} className="track-canvas" />

        <div className="lv-lb">
          <div className="lv-lb__head">
            <span className="lv-lb__title">
              {selectedFormulaId === 'ALL' ? 'Classifica' : availableFormulas.find(([id]) => id === selectedFormulaId)?.[1]}
            </span>
            <span className="lv-lb__count"><Icon.Users size={11} /> {leaderboard.length}</span>
          </div>
          <div className="lv-lb__list">
            {leaderboard.length === 0 ? (
              <div className="lv-lb__empty"><Icon.Clock size={16} /> In attesa dati GPS…</div>
            ) : (
              leaderboard.map((d, idx) => {
                const isLeader = idx === 0;
                const fastest = d.bestLapTime && globalBestLap && d.bestLapTime === globalBestLap;
                const animation = overtakeAnimations.get(keyOf(d));
                const animClass = animation ? `lv-row--${animation.type}` : '';
                const gap = selectedFormulaId === 'ALL' ? d.gapToLeader : (d.classGapToLeader || d.gapToLeader);
                return (
                  <div
                    key={keyOf(d)}
                    className={`lv-lb__row ${isLeader ? 'is-leader' : ''} ${animClass}`}
                    title={`${d.fullName} — Clicca per seguire`}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/pilot/${d.pilotId}`, { state: { from: location.pathname } })}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/pilot/${d.pilotId}`, { state: { from: location.pathname } }); }}
                  >
                    <span className={`rs-pos ${idx < 3 ? `rs-pos--${idx + 1}` : ''}`}>{idx + 1}</span>
                    <span className="lv-lb__team">
                      {d.type === 'team' && d.logoUrl
                        ? <img src={`${API_BASE}${d.logoUrl}`} alt={d.team} />
                        : <span className="rs-color-dot" style={{ background: colorsRef.current[keyOf(d)] || colorFromName(d.fullName || d.tag || d.mac) }} />}
                    </span>
                    <span className="lv-lb__name">{shortLabel(d)}</span>
                    <span className="lv-lb__icons">
                      {d.inPit && <span className="lv-pit">PIT</span>}
                      {fastest && <span className="lv-fast" title={`Best lap ${formatLap(d.bestLapTime)}`}><Icon.Zap size={11} /></span>}
                    </span>
                    <span className="lv-lb__gap">{isLeader ? <span className="lv-lb__leader">LEADER</span> : gap}</span>
                    <span className="lv-lb__laps">{d.lapCount}{hasTotalLaps && <small>/{totalLapsLabel}</small>}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      <div className="lv-body">
        {/* ═══ CLASSIFICA DETTAGLIATA ═══ */}
        <section className="rs-card lv-card">
          <div className="rs-card__head rs-card__head--toggle" onClick={() => toggleSection('detailedLeaderboard')}>
            <span className="rs-card__title"><Icon.Layers /> Classifica dettagliata
              <span className="lv-card__sub">{selectedFormulaId === 'ALL' ? 'Generale' : availableFormulas.find(([id]) => id === selectedFormulaId)?.[1]}</span>
            </span>
            <div className="lv-card__right">
              <span className="rs-chip"><Icon.Users /> <strong>{leaderboard.length}</strong> piloti</span>
              <Icon.ChevronDown className={`rs-chevron ${expandedSections.detailedLeaderboard ? 'rs-chevron--open' : ''}`} />
            </div>
          </div>

          {expandedSections.detailedLeaderboard && (
            <div className="rs-card__body--flush lv-table-wrap">
              {leaderboard.length === 0 ? (
                <div className="rs-empty lv-empty"><Icon.Clock size={24} /><strong>In attesa dei dati GPS…</strong></div>
              ) : (
                <table className="rs-table rs-table--dense lv-table">
                  <thead>
                    <tr>
                      <th className="num">P</th>
                      <th>Team</th>
                      <th>Pilota</th>
                      <th className="num">Gap</th>
                      <th>Pit</th>
                      <th className="num"><span className="lv-sector-dot" style={{ background: sectorColors[0] }} />S1</th>
                      <th className="num"><span className="lv-sector-dot" style={{ background: sectorColors[1] }} />S2</th>
                      <th className="num"><span className="lv-sector-dot" style={{ background: sectorColors[2] }} />S3</th>
                      <th className="num">Ultimo</th>
                      <th className="num">Migliore</th>
                      <th>Sanzioni</th>
                      <th className="num">Giri</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((d, idx) => {
                      const fastest = d.bestLapTime && globalBestLap && d.bestLapTime === globalBestLap;
                      const animation = overtakeAnimations.get(keyOf(d));
                      const animClass = animation ? `lv-row--${animation.type}` : '';
                      const isLeader = idx === 0;
                      const gap = selectedFormulaId === 'ALL' ? d.gapToLeader : (d.classGapToLeader || d.gapToLeader);
                      return (
                        <tr
                          key={`wide-${keyOf(d)}`}
                          className={`is-clickable ${animClass} ${isLeader ? 'is-leader' : ''}`}
                          title={`${d.fullName} — Clicca per seguire`}
                          onClick={() => navigate(`/pilot/${d.pilotId}`, { state: { from: location.pathname } })}
                        >
                          <td className="num"><span className={`rs-pos ${idx < 3 ? `rs-pos--${idx + 1}` : ''}`}>{idx + 1}</span></td>
                          <td>
                            {d.type === 'team' && d.logoUrl
                              ? <img src={`${API_BASE}${d.logoUrl}`} alt={d.team} className="lv-table__logo" />
                              : <span className="rs-color-dot" style={{ background: colorsRef.current[keyOf(d)] || colorFromName(d.fullName || d.tag || d.mac) }} />}
                          </td>
                          <td className="lv-table__driver">
                            <span className="lv-table__tag">{d.tag}</span>
                            <span className="lv-table__name">{d.fullName}</span>
                          </td>
                          <td className="num"><span className={`rs-time ${isLeader ? 'lv-table__leader' : ''}`}>{isLeader ? 'LEADER' : gap}</span></td>
                          <td>{d.inPit && <span className="lv-pit">PIT</span>}</td>
                          <td className="num rs-time">{formatLap(d.lastSectorTimes?.S1 ?? d.sectorTimes?.S1)}</td>
                          <td className="num rs-time">{formatLap(d.lastSectorTimes?.S2 ?? d.sectorTimes?.S2)}</td>
                          <td className="num rs-time">{formatLap(d.lastSectorTimes?.S3 ?? d.sectorTimes?.S3)}</td>
                          <td className="num rs-time">{formatLap(d.lastLapTime)}</td>
                          <td className={`num rs-time ${fastest ? 'rs-time--best' : ''}`}>
                            {formatLap(d.bestLapTime)}{fastest && <Icon.Zap size={11} className="lv-table__fast" />}
                          </td>
                          <td>
                            {d?.penalty?.summary && d.penalty.summary !== '—'
                              ? <span className="rs-badge rs-badge--danger">{d.penalty.summary}</span>
                              : <span className="rs-text-faint">—</span>}
                          </td>
                          <td className="num rs-time">{d.lapCount}{hasTotalLaps && <span className="rs-text-faint">/{totalLapsLabel}</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>

        {/* ═══ RACE CONTROL — solo direzione gara ═══ */}
        {!publicMode && (
          <section className="rs-card rs-card--accent lv-card lv-rc">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Flag /> Race control<span className="lv-card__sub">Direzione gara</span></span>
              <span className={`lv-status lv-status--${statusSlug}`}>
                <RaceFlag status={currentStatus} size={16} />
                <span>{currentStatus || 'IN CORSO'}</span>
              </span>
            </div>

            {/* START prominente durante il formation lap */}
            {isFormationLap && (
              <div className="lv-formation">
                <div className="lv-formation__text">
                  <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot rs-kicker__dot--live" />Formation lap</span>
                  <strong>I piloti stanno prendendo posizione.</strong>
                  <span>Premi START per avviare la gara{raceDurationMinutes ? ` · modalità timer: ${raceDurationMinutes} minuti` : ''}.</span>
                </div>
                <button className="rs-btn rs-btn--primary rs-btn--xl" onClick={startRaceFromFormation}>
                  <Icon.Play size={20} /> Start gara
                </button>
              </div>
            )}

            <div className="lv-rc__grid">
              {/* COLONNA 1: bandiere + zona pericolo */}
              <div className="lv-rc__col">
                <span className="rs-label">Stato gara</span>
                <div className="lv-flags">
                  {[
                    { s: 'IN CORSO', label: 'Verde', cls: 'green' },
                    { s: 'YELLOW FLAG', label: 'Gialla', cls: 'yellow' },
                    { s: 'RED FLAG', label: 'Rossa', cls: 'red' },
                    { s: 'FINITA', label: 'Scacchi', cls: 'chequered' },
                  ].map(f => (
                    <button
                      key={f.s}
                      className={`lv-flag lv-flag--${f.cls} ${currentStatus === f.s ? 'is-active' : ''}`}
                      onClick={() => sendStatus(f.s)}
                    >
                      <span className="lv-flag__swatch" />
                      <span className="lv-flag__label">{f.label}</span>
                    </button>
                  ))}
                </div>

                {Array.isArray(snapshot?.flagDevices) && snapshot.flagDevices.length > 0 && (
                  <div className="lv-flagcar">
                    <span className="rs-label">Vettura bandiera gialla</span>
                    {snapshot.flagDevices.map(f => (
                      <div key={f.mac} className={`lv-flagcar__item ${f.onTrack ? 'is-on' : ''}`}>
                        <span className="lv-flagcar__dot" />
                        <span className="lv-flagcar__mac">{f.mac}</span>
                        <span className="lv-flagcar__state">
                          {f.onTrack ? 'IN PISTA' : f.seen ? 'FUORI PISTA' : 'NESSUN DATO'}
                          {f.seen && f.lastSeenMs != null && f.lastSeenMs > 5000 && <em> · {Math.round(f.lastSeenMs / 1000)}s fa</em>}
                        </span>
                      </div>
                    ))}
                    <span className="rs-help">In pista → YELLOW FLAG automatica; fuori → verde. Una bandiera impostata a mano ha sempre la precedenza.</span>
                  </div>
                )}

                <span className="rs-label lv-rc__danger-label">Zona pericolo</span>
                {!showResetConfirm ? (
                  <button className="rs-btn rs-btn--warning rs-btn--block" onClick={() => setShowResetConfirm(true)} disabled={resetting}>
                    <Icon.Activity size={15} /> Reset completo
                  </button>
                ) : (
                  <div className="rs-confirm rs-confirm--warning">
                    <p className="rs-confirm__text">
                      <strong>Attenzione:</strong> questa azione cancella tempi, settori, sanzioni, traiettorie e ogni dato
                      della gara in corso. La gara ripartirà dalla <strong>formation lap</strong> con gli stessi piloti,
                      circuito e impostazioni. La registrazione corrente verrà chiusa e ne partirà una nuova.
                    </p>
                    <div className="rs-confirm__actions">
                      <button className="rs-btn rs-btn--ghost rs-btn--sm" onClick={() => setShowResetConfirm(false)} disabled={resetting}>Annulla</button>
                      <button
                        className="rs-btn rs-btn--warning rs-btn--sm"
                        disabled={resetting}
                        onClick={async () => {
                          setResetting(true);
                          try {
                            const r = await fetch(`${API_BASE}/api/race/reset`, { method: 'POST' });
                            if (!r.ok) {
                              const err = await r.json().catch(() => ({}));
                              alert(`Reset fallito: ${err.error || r.statusText}`);
                            }
                          } catch (e) {
                            alert(`Reset fallito: ${e.message}`);
                          } finally {
                            setResetting(false);
                            setShowResetConfirm(false);
                          }
                        }}
                      >
                        {resetting ? 'Reset in corso…' : 'Conferma reset'}
                      </button>
                    </div>
                  </div>
                )}

                {!showStopConfirm ? (
                  <button className="rs-btn rs-btn--danger rs-btn--block" onClick={() => setShowStopConfirm(true)}>
                    <Icon.X size={15} /> Termina gara
                  </button>
                ) : (
                  <div className="rs-confirm">
                    <p className="rs-confirm__text">Terminare la gara? La registrazione viene chiusa e salvata nell'archivio.</p>
                    <div className="rs-confirm__actions">
                      <button className="rs-btn rs-btn--ghost rs-btn--sm" onClick={() => setShowStopConfirm(false)}>Annulla</button>
                      <button className="rs-btn rs-btn--danger rs-btn--sm" onClick={async () => {
                        try { await fetch(`${API_BASE}/api/race/stop`, { method: 'POST' }) } catch { };
                        onStopRace();
                      }}>Conferma</button>
                    </div>
                  </div>
                )}
              </div>

              {/* COLONNA 2: sanzioni */}
              <div className="lv-rc__col">
                <span className="rs-label">Sanzioni</span>
                <select className="rs-select" value={penTargetId} onChange={(e) => setPenTargetMac(e.target.value)}>
                  <option value="">Seleziona pilota…</option>
                  {leaderboard.map(d => (
                    <option key={d.pilotId} value={d.pilotId}>P{d.position} · {d.fullName}</option>
                  ))}
                </select>

                {penTargetId && (
                  <>
                    <div className="lv-pen__pilot">
                      <span className="lv-pen__avatar">{penTarget?.tag?.slice(0, 3) || '?'}</span>
                      <div className="lv-pen__info">
                        <span className="lv-pen__name">{penTarget?.fullName || 'Pilota'}</span>
                        <span className="lv-pen__current">Penalità attuali: {penTarget?.penalty?.summary || 'Nessuna'}</span>
                      </div>
                    </div>
                    <textarea
                      className="rs-textarea"
                      value={penaltyReason}
                      onChange={(e) => setPenaltyReason(e.target.value)}
                      placeholder="Motivazione (opzionale)…"
                      rows={2}
                    />
                  </>
                )}

                {!showPenaltyConfirm ? (
                  <div className="lv-pen__grid">
                    <button className="rs-btn rs-btn--mono" disabled={!penTargetId} onClick={() => requestPenalty('+5s', '+5 secondi')}>+5s</button>
                    <button className="rs-btn rs-btn--mono" disabled={!penTargetId} onClick={() => requestPenalty('+10s', '+10 secondi')}>+10s</button>
                    <button className="rs-btn rs-btn--mono" disabled={!penTargetId} onClick={() => requestPenalty('+15s', '+15 secondi')}>+15s</button>
                    <button className="rs-btn rs-btn--mono rs-btn--warning" disabled={!penTargetId} onClick={() => requestPenalty('1mo avv. squalifica', 'Avvertimento')}><Icon.Alert size={13} /> AVV</button>
                    <button className="rs-btn rs-btn--mono rs-btn--danger" disabled={!penTargetId} onClick={() => requestPenalty('squalifica', 'Squalifica')}><Icon.X size={13} /> DSQ</button>
                  </div>
                ) : (
                  <div className="rs-confirm rs-confirm--warning">
                    <p className="rs-confirm__text">
                      Assegnare <strong>{showPenaltyConfirm.label}</strong> a <strong>{penTarget?.tag}</strong>?
                      {penaltyReason && <><br /><span className="rs-text-muted">Motivazione: {penaltyReason}</span></>}
                      <br /><span className="rs-text-faint"><Icon.Lock size={11} /> Le penalità non sono revocabili.</span>
                    </p>
                    <div className="rs-confirm__actions">
                      <button className="rs-btn rs-btn--ghost rs-btn--sm" onClick={() => setShowPenaltyConfirm(null)}>Annulla</button>
                      <button className="rs-btn rs-btn--warning rs-btn--sm" onClick={() => sendPenalty(showPenaltyConfirm.type)}>Conferma</button>
                    </div>
                  </div>
                )}
              </div>

              {/* COLONNA 3: violazioni velocità */}
              <div className="lv-rc__col">
                <span className="rs-label">Violazioni velocità {snapshot?.maxYellowFlagSpeed && <span className="rs-badge rs-badge--warning">Max {snapshot.maxYellowFlagSpeed} km/h</span>}</span>
                {!snapshot?.maxYellowFlagSpeed ? (
                  <div className="rs-empty lv-empty--sm"><Icon.Info size={20} /><span>Limite non configurato</span></div>
                ) : yellowFlagViolations.length === 0 ? (
                  <div className="rs-empty lv-empty--sm lv-empty--ok"><Icon.Check size={20} /><span>Nessuna violazione</span></div>
                ) : (
                  <div className="lv-viol">
                    {yellowFlagViolations.map((violation) => {
                      const violationTime = new Date(violation.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      return (
                        <div key={violation.id} className="lv-viol__item">
                          <div className="lv-viol__info">
                            <span className="lv-viol__name">{violation.fullName}</span>
                            <span className="lv-viol__meta"><span className="rs-num">{violationTime}</span> · <strong className="rs-num">{Math.round(violation.speed)}</strong> km/h</span>
                          </div>
                          <button className="rs-btn rs-btn--icon rs-btn--sm rs-btn--lime" onClick={() => acknowledgeViolation(violation.id)} title="Conferma presa visione">
                            <Icon.Check size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ═══ STRUMENTI — OBS / Export / Condivisione ═══ */}
        {!overlayMode && !publicMode && (
          <div className="rs-grid rs-grid--3 lv-tools">
            {/* OBS */}
            <section className="rs-card lv-card">
              <div className="rs-card__head">
                <span className="rs-card__title"><Icon.Monitor /> OBS overlay</span>
                <span className="rs-badge">{selectedObsCount} attivi</span>
              </div>
              <div className="rs-card__body lv-tool">
                <div className="lv-obs">
                  {[
                    { key: 'map', title: 'Mappa live', desc: 'Circuito con piloti in tempo reale', icon: Icon.Track },
                    { key: 'leaderboard', title: 'Classifica', desc: 'Rotazione automatica formule', icon: Icon.Layers },
                  ].map(o => {
                    const Ic = o.icon;
                    return (
                      <div className="lv-obs__row" key={o.key}>
                        <label className={`lv-obs__option ${obsSelections[o.key] ? 'is-on' : ''}`}>
                          <input type="checkbox" checked={!!obsSelections[o.key]} onChange={() => toggleObsSelection(o.key)} />
                          <span className="lv-obs__check"><Icon.Check size={12} /></span>
                          <span className="lv-obs__icon"><Ic size={16} /></span>
                          <span className="lv-obs__text"><strong>{o.title}</strong><span>{o.desc}</span></span>
                        </label>
                        <ObsFormatToggle on={!!obsVertical[o.key]} onToggle={() => toggleObsVertical(o.key)} title={o.title} />
                      </div>
                    );
                  })}
                  <span className="rs-label lv-obs__divider">Grafiche broadcast · green screen</span>
                  {[
                    { key: 'graphics', title: 'Regia automatica', desc: 'Scene a rotazione: stato, distacchi, giro veloce, battaglie, top speed, settori, battito' },
                    { key: 'graphics/status', title: 'Barra stato gara', desc: 'Bandiera, giro/timer, circuito ed evento' },
                    { key: 'graphics/gaps', title: 'Distacchi', desc: 'Classifica con barre dei gap dal leader' },
                    { key: 'graphics/fastest', title: 'Giro veloce', desc: 'Miglior giro assoluto con pilota e giro' },
                    { key: 'graphics/battle', title: 'Battaglia', desc: 'Duello più serrato con gap e tendenza' },
                    { key: 'graphics/topspeed', title: 'Top speed', desc: 'Velocità massime della sessione' },
                    { key: 'graphics/sectors', title: 'Settori migliori', desc: 'Best S1/S2/S3 e giro ideale' },
                    { key: 'graphics/podium', title: 'Podio', desc: 'Primi tre con distacchi (fine gara)' },
                    { key: 'graphics/driver', title: 'Scheda pilota', desc: 'Leader o pilota scelto con ?id=' },
                    { key: 'graphics/heart', title: 'Battito cardiaco', desc: 'BPM live, massimo e zona dei piloti con fascia' },
                  ].map(o => (
                    <div className="lv-obs__row" key={o.key}>
                      <label className={`lv-obs__option ${obsSelections[o.key] ? 'is-on' : ''}`}>
                        <input type="checkbox" checked={!!obsSelections[o.key]} onChange={() => toggleObsSelection(o.key)} />
                        <span className="lv-obs__check"><Icon.Check size={12} /></span>
                        <span className="lv-obs__icon"><Icon.Monitor size={16} /></span>
                        <span className="lv-obs__text"><strong>{o.title}</strong><span>{o.desc}</span></span>
                      </label>
                      <ObsFormatToggle on={!!obsVertical[o.key]} onToggle={() => toggleObsVertical(o.key)} title={o.title} />
                    </div>
                  ))}
                  <span className="rs-label lv-obs__divider">Dirette social simulate · green screen</span>
                  {[
                    { key: 'social/instagram', title: 'Instagram Live', desc: 'Interfaccia IG completa (commenti finti, badge, cuori) + regia e sponsor · 1080×1920' },
                    { key: 'social/youtube', title: 'YouTube Live · da PC', desc: 'Player, chat dal vivo, titolo e canale + regia e sponsor · 1920×1080' },
                    { key: 'social/youtube-verticale', title: 'YouTube Live · da telefono', desc: 'App YouTube in verticale con chat e Super Chat · 1080×1920' },
                  ].map(o => (
                    <div className="lv-obs__row" key={o.key}>
                      <label className={`lv-obs__option ${obsSelections[o.key] ? 'is-on' : ''}`}>
                        <input type="checkbox" checked={!!obsSelections[o.key]} onChange={() => toggleObsSelection(o.key)} />
                        <span className="lv-obs__check"><Icon.Check size={12} /></span>
                        <span className="lv-obs__icon"><Icon.Device size={16} /></span>
                        <span className="lv-obs__text"><strong>{o.title}</strong><span>{o.desc}</span></span>
                      </label>
                    </div>
                  ))}
                  <span className="rs-label lv-obs__divider">Sponsor · green screen</span>
                  {[
                    { key: 'sponsors', title: 'Carosello', desc: 'Tutti a rotazione con dissolvenza' },
                    { key: 'sponsors/banner', title: 'Banner', desc: 'Tutti i loghi in barra inferiore' },
                    { key: 'sponsors/ticker', title: 'Ticker', desc: 'Loghi che scorrono in continuo' },
                  ].map(o => (
                    <div className="lv-obs__row" key={o.key}>
                      <label className={`lv-obs__option ${obsSelections[o.key] ? 'is-on' : ''}`}>
                        <input type="checkbox" checked={!!obsSelections[o.key]} onChange={() => toggleObsSelection(o.key)} />
                        <span className="lv-obs__check"><Icon.Check size={12} /></span>
                        <span className="lv-obs__icon"><Icon.Star size={16} /></span>
                        <span className="lv-obs__text"><strong>{o.title}</strong><span>{o.desc}</span></span>
                      </label>
                      <ObsFormatToggle on={!!obsVertical[o.key]} onToggle={() => toggleObsVertical(o.key)} title={o.title} />
                    </div>
                  ))}
                </div>
                <button className="rs-btn rs-btn--primary rs-btn--block" onClick={handleLaunchObsWindows} disabled={!selectedObsCount}>
                  <Icon.Share size={15} /> Apri overlay
                </button>
                {sponsors.length > 0 && (
                  <div className="lv-obs__singles">
                    <div className="lv-obs__singles-head">
                      <span className="rs-label">Overlay singolo sponsor</span>
                      <ObsFormatToggle on={!!obsVertical.sponsor} onToggle={() => toggleObsVertical('sponsor')} title="Singolo sponsor" />
                    </div>
                    <div className="lv-obs__chips">
                      {sponsors.map(sp => (
                        <button key={sp.id} type="button" className="rs-btn rs-btn--sm" title={`Apri overlay ${sp.name}`}
                          onClick={() => window.open(withFormat(`${window.location.origin}/obs/sponsor/${sp.id}`, !!obsVertical.sponsor), '_blank', 'noopener,noreferrer')}>
                          {sp.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <p className="rs-help">Le <strong>dirette social simulate</strong> servono ai video promozionali: disegnano l'interfaccia di Instagram o YouTube sopra un fondo verde, con commenti finti e le grafiche di gara vere. In montaggio si sostituisce il verde con la clip ripresa in pista. Parametri: <code>?user=</code>, <code>?channel=</code>, <code>?title=</code>, <code>?viewers=</code>, <code>?comments=4</code> (secondi fra un commento e l'altro, 0 li toglie), <code>?graphics=0</code>, <code>?ticker=0</code>.</p>
                <p className="rs-help"><strong>9:16</strong> apre l'overlay in verticale per le dirette Instagram / YouTube: in OBS usa una Browser Source 1080×1920. La grafica è compatta e sta nella fascia bassa dello schermo, sopra i commenti, così non copre la diretta. <code>?band=320</code> cambia l'altezza della fascia, <code>?pos=tc</code> la sposta in alto, <code>?safe=0</code> usa tutto lo schermo.</p>
                <p className="rs-help">Tutti gli overlay hanno sfondo verde per il Chroma Key di OBS (verde <code>#00ff00</code>). Se ti serve lo sfondo trasparente aggiungi <code>?bg=transparent</code> all'URL; <code>?pos=br</code> cambia la posizione delle grafiche. Gestisci i loghi in <strong>Sponsor</strong>.</p>
              </div>
            </section>

            {/* Export XML */}
            <section className="rs-card lv-card">
              <div className="rs-card__head">
                <span className="rs-card__title"><Icon.Archive /> Export XML</span>
                <span className="rs-badge">{Object.values(exportSelections).filter(Boolean).length} sel.</span>
              </div>
              <div className="rs-card__body lv-tool">
                <div className="lv-export">
                  {rankingOptions.map(opt => (
                    <label className={`lv-obs__option ${exportSelections[opt.id] ? 'is-on' : ''}`} key={opt.id}>
                      <input type="checkbox" checked={!!exportSelections[opt.id]} onChange={() => toggleExportSelection(opt.id)} />
                      <span className="lv-obs__check"><Icon.Check size={12} /></span>
                      <span className="lv-obs__text"><strong>{opt.label}</strong><span>{opt.count || 0} piloti</span></span>
                    </label>
                  ))}
                </div>
                <button className="rs-btn rs-btn--primary rs-btn--block" onClick={handleExportXml} disabled={!canExportXml}>
                  <Icon.Archive size={15} /> Esporta XML
                </button>
                <p className="rs-help">Classifiche per la regia: compatibile con i sistemi di grafica TV/broadcast.</p>
              </div>
            </section>

            {/* Live share */}
            <section className="rs-card lv-card">
              <div className="rs-card__head">
                <span className="rs-card__title"><Icon.Share /> Live share</span>
                <span className="rs-badge rs-badge--red"><span className="rs-badge__dot rs-badge__dot--live" />Live</span>
              </div>
              <div className="rs-card__body lv-tool lv-share">
                <span className="rs-label">Link pubblico</span>
                <div className="rs-input-group">
                  <input type="text" className="rs-input rs-input--mono lv-share__input" value={publicUrl} readOnly onClick={(e) => e.target.select()} />
                  <button className="rs-btn" onClick={copyPublicLink} title="Copia link"><Icon.Share size={14} /> Copia</button>
                </div>
                <div className="lv-share__qr">
                  <div className="lv-share__qr-box">
                    <QRCodeCanvas
                      id="live-share-qr-canvas"
                      value={publicUrl}
                      size={140}
                      level="H"
                      includeMargin={true}
                      bgColor="#ffffff"
                      fgColor="#0a0a0c"
                      imageSettings={{ src: raceSenseLogo, x: undefined, y: undefined, height: 35, width: 35, excavate: true }}
                    />
                  </div>
                  <div className="lv-share__qr-text">
                    <span>Scansiona per seguire la gara in tempo reale su qualsiasi dispositivo.</span>
                    <button className="rs-btn rs-btn--sm" onClick={downloadQrPdf} title="Scarica QR Code come PDF"><Icon.Archive size={14} /> Scarica PDF</button>
                  </div>
                </div>
                <div className="lv-share__features">
                  <span className="rs-chip"><Icon.Zap /> WebSocket real-time</span>
                  <span className="rs-chip"><Icon.Lock /> Solo visualizzazione</span>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Avvisi bandiera gialla — vista pubblica */}
        {publicMode && yellowFlagViolations.length > 0 && snapshot?.raceStatus === 'YELLOW FLAG' && (
          <section className="rs-card rs-card--accent-warning lv-card">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Alert /> Avvisi bandiera gialla</span>
              <span className="rs-badge rs-badge--warning">Max {snapshot?.maxYellowFlagSpeed} km/h</span>
            </div>
            <div className="rs-card__body">
              <div className="lv-viol lv-viol--public">
                {yellowFlagViolations.map((violation) => {
                  const timestamp = new Date(violation.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return (
                    <div key={violation.id} className="lv-viol__item">
                      <div className="lv-viol__info">
                        <span className="lv-viol__name">{violation.fullName}</span>
                        <span className="lv-viol__meta"><span className="rs-num">{timestamp}</span> · <strong className="rs-num">{Math.round(violation.speed)}</strong> km/h <span className="rs-text-faint">(limite {violation.maxAllowed})</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ═══ GESTIONE DISPOSITIVI LIVE — solo direzione gara, chiuso di default ═══ */}
        {!overlayMode && !publicMode && (
          <DeviceManagerPanel
            drivers={snapshot?.drivers || []}
            flagDevices={snapshot?.flagDevices || []}
            expanded={expandedSections.devices}
            onToggle={() => toggleSection('devices')}
          />
        )}
      </div>
    </div>
  );
}
